/* ============================================================
   Long Run Co. — Member Portal (PHASE 1)
   Live Appstle /cp/ reads bound into the v7-ported layout.
   Auth = the customer's Shopify session (App Proxy). All
   write/confirm actions are INERT (LRCMP.noop) this phase —
   modals open/close as UI only.

   CONSTRAINT: totalSuccessfulOrders & lifetimeValue are never
   written to the DOM. lifetimeValue is read into _internal only.

   Summary math (reconciles in every case):
     Subtotal       = Σ retail (compare-at) line totals
     Member savings = retail − member price
     Discount applied = Σ real discountAllocations (hidden if 0)
     Shipping       = 0 when member subtotal ≥ threshold, else deliveryPrice.amount
     Total          = Σ(member price×qty) − real allocations + shipping
   Identity: Subtotal − Member savings − Discount + Shipping == Total
   ============================================================ */
(function () {
  "use strict";

  var root = document.querySelector("[data-lrc-member-portal]");
  if (!root) return;

  var BASE = (root.getAttribute("data-proxy-base") || "/apps/subscriptions/cp/api/").replace(/\/+$/, "") + "/";
  var FREE_SHIP = parseFloat(root.getAttribute("data-free-ship-threshold"));
  if (isNaN(FREE_SHIP)) FREE_SHIP = 49;

  // Theme-editor design mode: render the full portal with SAMPLE data so the
  // merchant can style every module. Sample data is NEVER shown to a real
  // visitor — only when design mode is detected.
  var DESIGN = (root.getAttribute("data-design-mode") === "true") ||
               (typeof window.Shopify !== "undefined" && window.Shopify && window.Shopify.designMode === true);

  var _internal = { lifetimeValue: null }; // intentionally never rendered

  /* ---------- helpers ---------- */
  function region(name) { return root.querySelector('[data-mp-region="' + name + '"]'); }
  function has(name) { return !!region(name); }
  function show(el) { if (el) el.classList.remove("lrc-mp__hide"); }
  function hide(el) { if (el) el.classList.add("lrc-mp__hide"); }
  function setText(name, val) { var el = region(name); if (el) el.textContent = (val == null || val === "") ? "—" : val; }
  function setHTML(name, html) { var el = region(name); if (el) el.innerHTML = html; }
  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
    return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]; }); }
  function round2(n) { return Math.round((n + Number.EPSILON) * 100) / 100; }
  function num(v) { var n = parseFloat(v); return isNaN(n) ? null : n; }

  /* ---- Tier-aware pricing: shared helpers (exposed on window so the Members-Only inline script reuses them) ---- */
  /* Map the customer's current plan id to the plan whose allocation we display/add against.
     Stash 6mo/12mo aren't for one-time drops → fall back to the 25% Tier / 90-day plan. */
  window.lrcMatchPlanForCustomer = function (customerPlanIdNumeric) {
    if (customerPlanIdNumeric === "702230561081" || customerPlanIdNumeric === "702230593849") return "692168458553"; /* 25% Tier, 90 Days */
    return customerPlanIdNumeric;
  };
  /* The active contract's first line plan id (numeric, gid-stripped, matcher-applied), or null. */
  window.lrcCustomerPlanId = function () {
    try {
      var subs = window.LRCMP_SUBS, c = subs && subs.contracts && subs.contracts[window.LRCMP_SUB_ACTIVE || 0];
      var contract = c && c.contract, ln = contract && (contract.lines && (contract.lines.nodes || contract.lines));
      var first = ln && ln[0];
      var raw = first && (first.sellingPlanId || (first.sellingPlan && first.sellingPlan.id));
      if (!raw) return null;
      var m = String(raw).match(/(\d+)\s*$/); if (!m) return null;
      return window.lrcMatchPlanForCustomer(m[1]);
    } catch (e) { return null; }
  };

  function api(path) {
    return fetch(BASE + path.replace(/^\/+/, ""), {
      method: "GET", credentials: "same-origin", headers: { "Accept": "application/json" }
    }).then(function (res) {
      if (res.status === 401 || res.status === 403) { var e = new Error("unauthenticated"); e.code = 401; throw e; }
      if (!res.ok) { var e2 = new Error("HTTP " + res.status); e2.code = res.status; throw e2; }
      var ct = res.headers.get("content-type") || "";
      return ct.indexOf("json") > -1 ? res.json() : res.text();
    });
  }
  function apiSafe(path) { return api(path).catch(function () { return null; }); }

  function pick(obj, paths, dflt) {
    for (var i = 0; i < paths.length; i++) {
      var cur = obj, ok = true, parts = paths[i].split(".");
      for (var j = 0; j < parts.length; j++) {
        if (cur != null && typeof cur === "object" && parts[j] in cur) cur = cur[parts[j]];
        else { ok = false; break; }
      }
      if (ok && cur != null && cur !== "") return cur;
    }
    return dflt;
  }
  function toArray(x) {
    if (Array.isArray(x)) return x;
    if (x == null) return [];
    if (typeof x === "object") {
      var keys = ["content", "data", "items", "results", "list", "rewards", "redeemOptions", "redemptionOptions", "earnOptions", "options"];
      for (var k = 0; k < keys.length; k++) if (Array.isArray(x[keys[k]])) return x[keys[k]];
      return [x];
    }
    return [];
  }
  function moneyVal(m) {
    if (m == null) return null;
    if (typeof m === "number") return m;
    if (typeof m === "string") return num(m);
    if (typeof m === "object") { if (m.amount != null) return moneyVal(m.amount); if (m.value != null) return moneyVal(m.value); }
    return null;
  }
  function currencyOf(m, fb) {
    if (m && typeof m === "object") {
      if (m.currencyCode) return m.currencyCode;
      if (m.amount && typeof m.amount === "object" && m.amount.currencyCode) return m.amount.currencyCode;
    }
    return fb || "USD";
  }
  function fmtMoney(amount, currency) {
    if (amount == null) return null;
    try { return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" }).format(amount); }
    catch (e) { return "$" + Number(amount).toFixed(2); }
  }
  var DOW = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"], MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  var MON_FULL = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  /* Address — country + province/state code maps (ISO 3166; Appstle expects codes, not full names). */
  var ADDR_COUNTRIES = [
    { code: "US", name: "United States" },
    { code: "CA", name: "Canada" }
  ];
  var ADDR_PROVINCES = {
    US: [
      { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
      { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
      { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "DC", name: "District of Columbia" },
      { code: "FL", name: "Florida" }, { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" },
      { code: "ID", name: "Idaho" }, { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" },
      { code: "IA", name: "Iowa" }, { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" },
      { code: "LA", name: "Louisiana" }, { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" },
      { code: "MA", name: "Massachusetts" }, { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" },
      { code: "MS", name: "Mississippi" }, { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" },
      { code: "NE", name: "Nebraska" }, { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" },
      { code: "NJ", name: "New Jersey" }, { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" },
      { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" },
      { code: "OK", name: "Oklahoma" }, { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" },
      { code: "RI", name: "Rhode Island" }, { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" },
      { code: "TN", name: "Tennessee" }, { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" },
      { code: "VT", name: "Vermont" }, { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" },
      { code: "WV", name: "West Virginia" }, { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
      { code: "AS", name: "American Samoa" }, { code: "GU", name: "Guam" }, { code: "MP", name: "Northern Mariana Islands" },
      { code: "PR", name: "Puerto Rico" }, { code: "VI", name: "U.S. Virgin Islands" }
    ],
    CA: [
      { code: "AB", name: "Alberta" }, { code: "BC", name: "British Columbia" }, { code: "MB", name: "Manitoba" },
      { code: "NB", name: "New Brunswick" }, { code: "NL", name: "Newfoundland and Labrador" }, { code: "NS", name: "Nova Scotia" },
      { code: "ON", name: "Ontario" }, { code: "PE", name: "Prince Edward Island" }, { code: "QC", name: "Quebec" },
      { code: "SK", name: "Saskatchewan" }, { code: "NT", name: "Northwest Territories" }, { code: "NU", name: "Nunavut" },
      { code: "YT", name: "Yukon" }
    ]
  };
  function parseDate(v) { if (!v) return null; var d = new Date(v); return isNaN(d.getTime()) ? null : d; }
  function fmtDate(v, dow) { var d = parseDate(v); if (!d) return null; var s = MON[d.getMonth()] + " " + d.getDate(); return dow ? (DOW[d.getDay()] + " · " + s) : s; }
  function fmtSince(v) { var d = parseDate(v); if (!d) return null; return MON[d.getMonth()] + " " + d.getFullYear(); }
  function daysSince(v) { var d = parseDate(v); if (!d) return null; var ms = Date.now() - d.getTime(); return ms < 0 ? 0 : Math.floor(ms / 86400000); }
  function intervalLabel(interval, count) {
    if (!interval) return null;
    var unit = String(interval).toLowerCase().replace(/s$/, ""), n = num(count) || 1;
    var plural = { day: "days", week: "weeks", month: "months", year: "years" }[unit] || (unit + "s");
    return n === 1 ? ("Every " + unit) : ("Every " + n + " " + plural);
  }
  function cap(s) { s = String(s || "").toLowerCase(); return s.charAt(0).toUpperCase() + s.slice(1); }

  var state = { customerId: null, details: [], active: 0, cache: {} };

  /* ======================================================
     BOOT
     ====================================================== */
  function boot() {
    if (DESIGN) { bootDesign(); return; }
    api("logged-in-customer")
      .then(function (cid) {
        var id = num(typeof cid === "object" ? pick(cid, ["customerId", "id"]) : cid);
        if (!id || id <= 0) throw { code: 401 };
        state.customerId = id;
        try { sessionStorage.removeItem("lrcLoginTried"); } catch (e) {}
        return api("subscription-customers-detail/valid/" + id);
      })
      .then(function (detailRes) { state.details = toArray(detailRes).filter(Boolean); renderReady(); })
      .catch(function (err) {
        if (err && err.code === 401) signedOut();
        else errorState(err && err.message ? err.message : "Unknown error");
      });

    loadLoyalty(); // always fetch + publish for the standalone Rewards section
  }

  function renderReady() {
    hide(root.querySelector("[data-mp-loading]"));
    show(root.querySelector("[data-mp-app]"));
    if (!state.details.length) {
      publish({ state: "empty" });
      publishSubs({ state: "empty", contracts: [] });
      return;
    }
    publishIdentity();
    // Single fetcher: load every valid contract's bundle ONCE, then publish the
    // whole subscription dataset for the standalone subscription section to render
    // (the selector switches client-side; no refetch per tab).
    Promise.all(state.details.map(function (d) {
      var cid = pick(d, ["subscriptionContractId", "contractId", "id"], null);
      if (cid == null) return Promise.resolve({ dto: d, contract: null, payment: null });
      return Promise.all([
        apiSafe("subscription-contracts/contract/" + cid),
        apiSafe("subscription-payment-infos/" + cid)
      ]).then(function (r) { return { dto: d, contract: r[0], payment: r[1] }; });
    })).then(function (bundles) {
      state.bundles = bundles;
      publishSubs({ state: "ready", contracts: bundles });
      publishSavedFrom(bundles[0]); // identity "You've saved" from the primary contract
    });
  }

  /* ======================================================
     DESIGN MODE — realistic SAMPLE data (editor only)
     ====================================================== */
  function sampleDate(days) { var t = new Date(); t.setDate(t.getDate() + days); return t.toISOString(); }
  function sLine(title, variant, qty, price, compare) {
    var n = { title: title, variantTitle: variant, quantity: qty, currentPrice: { amount: String(price), currencyCode: "USD" } };
    if (compare != null) n.pricingPolicy = { basePrice: { amount: String(compare), currencyCode: "USD" } };
    return n;
  }
  var SAMPLE = {
    details: [
      { subscriptionContractId: "S1", customerFirstName: "Thomas", activatedOn: "2025-05-01T00:00:00Z",
        billingPolicyInterval: "DAY", billingPolicyIntervalCount: 30, deliveryPolicyInterval: "DAY", deliveryPolicyIntervalCount: 30,
        nextBillingDate: sampleDate(12), currencyCode: "USD", status: "ACTIVE", lifetimeValue: 397 },
      { subscriptionContractId: "S2", customerFirstName: "Thomas", activatedOn: "2025-05-01T00:00:00Z",
        billingPolicyInterval: "DAY", billingPolicyIntervalCount: 45, deliveryPolicyInterval: "DAY", deliveryPolicyIntervalCount: 45,
        nextBillingDate: sampleDate(26), currencyCode: "USD", status: "ACTIVE" }
    ],
    bundle: function (cid) {
      var two = (cid === "S2");
      var addr = { name: "Thomas Reed", firstName: "Thomas", lastName: "Reed", address1: "1240 Pearl St", city: "Boulder", provinceCode: "CO", zip: "80302", phone: "(720) 555-0142" };
      var lines = two
        ? [ sLine("Marathon Mango Pouches", "15 ct", 3, 5.99, 7.99) ]
        : [ sLine("The Long Run — Dark", "Whole bean · 12oz", 1, 35.99, 47.98) ];
      return {
        contract: {
          deliveryPrice: { amount: "7.99", currencyCode: "USD" },
          billingPolicy: { interval: "DAY", intervalCount: two ? 45 : 30 },
          deliveryMethod: { address: addr },
          lines: { nodes: lines }
        },
        payment: { cardBrand: "visa", cardLastDigits: "1234", cardExpiryMonth: 8, cardExpiryYear: 2027 }
      };
    },
    loyalty: { availablePointsBalance: 3277 },
    redeem: [
      { title: "Free 12oz bag", pointsCost: 2000 },
      { title: "Travel tumbler", pointsCost: 3500 },
      { title: "Members hoodie", pointsCost: 5000 }
    ],
    earn: [
      { title: "Subscribe to texts", description: "Order & shipment alerts", points: 200, completed: true },
      { title: "Review a product", description: "Tell us how it fuels you", points: 150 },
      { title: "Follow on Instagram", description: "@longrunco", points: 100 }
    ]
  };

  function bootDesign() {
    hide(root.querySelector("[data-mp-loading]"));
    show(root.querySelector("[data-mp-app]"));
    state.details = SAMPLE.details;
    publishIdentity();
    var bundles = SAMPLE.details.map(function (d) {
      var b = SAMPLE.bundle(pick(d, ["subscriptionContractId"], null));
      return { dto: d, contract: b.contract, payment: b.payment };
    });
    state.bundles = bundles;
    publishSubs({ state: "ready", contracts: bundles });
    publishSavedFrom(bundles[0]);
    publishLoyalty(SAMPLE.loyalty, SAMPLE.redeem, SAMPLE.earn);
    fillDesignExtras();
  }

  // Sample content for the JS-driven regions of the off-by-default modules so
  // they're visible/stylable in the editor.
  function fillDesignExtras() {
    if (has("campaign-slot")) setHTML("campaign-slot",
      '<div class="card slot-launch"><span class="pill">Members get it first</span>' +
      '<div class="eyebrow light">New release · early access</div><h2>The Midnight Series</h2>' +
      '<div class="sub" style="color:rgba(255,255,255,.82)">Three new blends, yours before anyone else.</div>' +
      '<div class="countdown mono">◷ PUBLIC LAUNCH IN 5 DAYS</div></div>');

    if (has("members-tiles")) setHTML("members-tiles",
      ['F.X1 // GUJI|Ethiopia, Guji|$24', 'F.X2 // NARIÑO|Colombia, Nariño|$22', 'F.X3 // RESERVE|Comp Reserve|$28']
      .map(function (t, i) { var p = t.split("|"); var cap = ["g", "r", "b"][i];
        return '<div class="ptile on-dark lift"><div class="cap ' + cap + '"></div><div class="pc mono">' + esc(p[0]) +
          '</div><div class="pn">' + esc(p[1]) + '</div><div class="pp mono">' + esc(p[2]) +
          '</div><button class="btn btn-primary btn-sm btn-block" onclick="LRCMP.noop(event)">Add</button></div>'; }).join(""));

    if (has("add-products")) setHTML("add-products",
      ['The Long Run — Dark|$26.99|$35.99', 'Marathon Mango Pouches|$5.99|$7.99', 'Recovery Tea|$13.49|']
      .map(function (t) { var p = t.split("|");
        return '<div class="ptile"><div class="cap b"></div><div class="pn">' + esc(p[0]) + '</div><div class="pp mono">' +
          esc(p[1]) + (p[2] ? '<span class="was">' + esc(p[2]) + '</span>' : "") +
          '</div><button class="btn btn-primary btn-sm btn-block" onclick="LRCMP.noop(event)">Add</button></div>'; }).join(""));
  }

  /* ---------- IDENTITY (data publisher) ----------
     Identity now lives in its own section (sections/lrc-mp-identity.liquid).
     This portal JS stays the single fetcher and PUBLISHES the identity data
     so that section can render it — no duplicate cp API calls. The latest
     payload is on window.LRCMP_DATA and a "lrc-mp:identity" event fires on
     every update. */
  function publish(patch) {
    state.identity = Object.assign(state.identity || {}, patch);
    window.LRCMP_DATA = state.identity;
    try { document.dispatchEvent(new CustomEvent("lrc-mp:identity", { detail: state.identity })); } catch (e) {}
  }
  function publishIdentity() {
    var d = state.details[0] || {};
    _internal.lifetimeValue = num(pick(d, ["lifetimeValue", "lifetimeValueUSD"], null)); // internal only
    publish({
      state: "ready",
      firstName: pick(d, ["customerFirstName", "customer.firstName"], "") || "",
      memberSinceText: fmtSince(pick(d, ["activatedOn", "startsAt", "createdAt"], null)) || "",
      daysFueled: daysSince(pick(d, ["createdAt", "startsAt", "activatedOn"], null))
    });
  }

  /* ---------- SUBSCRIPTION (data publisher) ----------
     Subscription now lives in its own section (sections/lrc-mp-subscription.liquid).
     Portal JS stays the single fetcher and publishes the full contract dataset
     on window.LRCMP_SUBS + a "lrc-mp:subscription" event. */
  /* Status-priority ordering (active=0, paused=1, cancelled=2), tie-broken by
     nextBillingDate ascending. Decorate-sort-undecorate keeps it STABLE so equal
     keys preserve their incoming order. Runs once at hydration so LRCMP_SUB_ACTIVE
     default 0 lands on a non-cancelled sub when one exists. */
  function statusRank(b) {
    var st = String(((b && b.dto) || {}).status || "").toLowerCase();
    return st === "active" ? 0 : st === "paused" ? 1 : st === "cancelled" ? 2 : 3;
  }
  function nextBillMs(b) {
    var d = (b && b.dto) || {}, c = (b && b.contract) || {};
    var v = d.nextBillingDate || d.nextPaidBillingDate || c.nextBillingDate;
    var t = v ? new Date(v).getTime() : NaN;
    return isNaN(t) ? Infinity : t;                                   /* missing date sorts last within its status */
  }
  function sortContracts(list) {
    if (!Array.isArray(list)) return list;
    return list.map(function (b, i) { return { b: b, i: i }; })
      .sort(function (x, y) {
        var dr = statusRank(x.b) - statusRank(y.b); if (dr) return dr;
        var dd = nextBillMs(x.b) - nextBillMs(y.b); if (dd) return dd;
        return x.i - y.i;                                             /* stable tiebreak on original index */
      })
      .map(function (o) { return o.b; });
  }
  function publishSubs(patch) {
    if (patch && Array.isArray(patch.contracts)) patch.contracts = sortContracts(patch.contracts);
    state.subs = Object.assign(state.subs || {}, patch);
    window.LRCMP_SUBS = state.subs;
    try { document.dispatchEvent(new CustomEvent("lrc-mp:subscription", { detail: state.subs })); } catch (e) {}
  }
  // identity "You've saved" — LIFETIME ESTIMATE from the member's current subscription:
  //   perOrderSavings = subscriptionDiscount + shippingSaved + sampleSaved
  //   lifetimeEstimate = round2(orderCount * perOrderSavings + gear)
  function publishSavedFrom(bundle) {
    if (!bundle) return;
    var c = bundle.contract || {}, dto = bundle.dto || {};
    var currency = pick(dto, ["currencyCode"], null) || currencyOf(c.deliveryPrice, "USD");

    // --- subscriptionDiscount: the REAL per-shipment discount (unchanged calc) ---
    var lines = toArray(pick(c, ["lines.nodes", "lines"], []));
    var retail = 0, member = 0, alloc = 0;
    lines.forEach(function (ln) {
      var qty = num(pick(ln, ["quantity"], 1)) || 1;
      var unit = moneyVal(pick(ln, ["currentPrice", "lineDiscountedPrice.pricePerUnit"], null)) || 0;
      var cmp = moneyVal(pick(ln, ["pricingPolicy.basePrice"], null));
      member += unit * qty; retail += ((cmp != null && cmp > unit) ? cmp : unit) * qty;
      alloc += toArray(pick(ln, ["discountAllocations"], [])).reduce(function (a, x) {
        return a + (moneyVal(pick(x, ["amount", "discountAmount", "amount.amount"], null)) || 0); }, 0);
    });
    var subscriptionDiscount = round2((retail - member) + alloc);
    // fallback when the real discount is 0/unavailable: 25% of the member subtotal / 0.75
    if (!(subscriptionDiscount > 0)) subscriptionDiscount = (member > 0) ? round2((member * 0.25) / 0.75) : 0;

    // --- shippingSaved: free ship on this contract (deliveryPrice 0/null) → 7.99, else 0 ---
    var shipPrice = moneyVal(pick(c, ["deliveryPrice"], null));
    var shippingSaved = (shipPrice == null || shipPrice === 0) ? 7.99 : 0;

    // --- sampleSaved: flat per order ---
    var sampleSaved = 4.99;

    // --- gear: 60 once subscribed > 90 days (same contract-start basis as daysFueled) ---
    var daysSinceStart = daysSince(pick(dto, ["createdAt", "startsAt", "activatedOn"], null));
    var gear = (daysSinceStart != null && daysSinceStart > 90) ? 60 : 0;

    // --- orderCount: prefer a real fulfilled-order count, else derive from days / cadence ---
    var orderCount = num(pick(dto, ["totalSuccessfulOrders", "successfulOrdersCount", "orderCount", "totalOrders"], null));
    if (!(orderCount > 0)) {
      // cadence days: delivery policy (fall back to billing), SAME field paths as amActiveIsPrepaid,
      // SAME interval→days mapping the forecast uses (DAY 1 / WEEK 7 / MONTH 30 / YEAR 365).
      var unit = String((c.deliveryPolicy && c.deliveryPolicy.interval) || dto.deliveryPolicyInterval ||
                        (c.billingPolicy && c.billingPolicy.interval) || dto.billingPolicyInterval || "").toUpperCase();
      var cnt = num((c.deliveryPolicy && c.deliveryPolicy.intervalCount != null) ? c.deliveryPolicy.intervalCount :
                (dto.deliveryPolicyIntervalCount != null ? dto.deliveryPolicyIntervalCount :
                ((c.billingPolicy && c.billingPolicy.intervalCount != null) ? c.billingPolicy.intervalCount : dto.billingPolicyIntervalCount)));
      var perUnit = { DAY: 1, WEEK: 7, MONTH: 30, YEAR: 365 }[unit];
      var cadenceDays = (perUnit != null && cnt > 0) ? perUnit * cnt : null;
      orderCount = (cadenceDays && daysSinceStart != null) ? Math.max(1, Math.floor(daysSinceStart / cadenceDays) + 1) : 1;
    }

    var perOrderSavings = subscriptionDiscount + shippingSaved + sampleSaved;
    var lifetimeEstimate = round2(orderCount * perOrderSavings + gear);

    console.log('[lrc savings est]', { orderCount: orderCount, discountPerOrder: subscriptionDiscount, shippingSaved: shippingSaved, sampleSaved: sampleSaved, gear: gear, lifetimeEstimate: lifetimeEstimate });

    publish({ savedAmount: lifetimeEstimate, currency: currency, savedText: fmtMoney(lifetimeEstimate, currency) });
  }


  /* ---------- LOYALTY (data publisher) ----------
     Rewards now lives in its own section (sections/lrc-mp-rewards.liquid).
     Portal JS stays the single fetcher and publishes the raw loyalty data on
     window.LRCMP_LOYALTY + a "lrc-mp:loyalty" event. */
  function loadLoyalty() {
    Promise.all([
      apiSafe("loyalty-integration/customer"),
      apiSafe("loyalty-integration/redeem-options"),
      apiSafe("loyalty-integration/earn-options")
    ]).then(function (r) { publishLoyalty(r[0], r[1], r[2]); });
  }
  function publishLoyalty(cust, redeem, earn) {
    state.loyalty = { state: "ready", customer: cust || null, redeem: redeem || null, earn: earn || null };
    window.LRCMP_LOYALTY = state.loyalty;
    try { document.dispatchEvent(new CustomEvent("lrc-mp:loyalty", { detail: state.loyalty })); } catch (e) {}
  }

  /* ---------- terminal states ---------- */
  function signedOut() {
    try {
      var el = root.querySelector("[data-login-url]") || root;
      var loginUrl = el && el.getAttribute("data-login-url");
      if (loginUrl && !sessionStorage.getItem("lrcLoginTried")) {
        sessionStorage.setItem("lrcLoginTried", "1");
        window.location.assign(loginUrl);
        return;
      }
    } catch (e) {}
    hide(root.querySelector("[data-mp-loading]")); hide(root.querySelector("[data-mp-app]"));
    show(root.querySelector("[data-mp-signedout]"));
    publish({ state: "signedout" });
  }
  function errorState(msg) {
    hide(root.querySelector("[data-mp-loading]")); hide(root.querySelector("[data-mp-app]"));
    show(root.querySelector("[data-mp-error]"));
    publish({ state: "error" });
    var d = root.querySelector("[data-mp-error-detail]");
    if (d && msg) d.textContent = d.textContent + " (" + msg + ")";
    if (window.console) console.warn("[lrc-member-portal]", msg);
  }

  /* ======================================================
     LRCMP — interaction layer (modals open/close; SAVES INERT)
     ====================================================== */
  function modal(id) { return root.querySelector('[data-modal="' + id + '"]'); }
  var LRCMP = {
    noop: function (e) { if (e && e.preventDefault) e.preventDefault(); return false; },
    swapMode: null,   /* { lineId, contractId } while a Swap-initiated add is in flight; cleared on add success or modal dismiss */
    open: function (id) { var m = modal(id); if (m) m.classList.add("show"); },
    close: function (id) { var m = modal(id); if (m) m.classList.remove("show"); if (id === "addModal") { LRCMP.swapMode = null; AM.busy = false; if (m) m.classList.remove("is-am-busy"); } },
    openAdd: function (opts) { amOpen(opts); },   /* tabbed pillared picker — see AM machinery below. Legacy arg shapes ignored gracefully. */
    openEdit: function (idx, lineId) {
      /* Read the line from the published data model — the items list lives in a separate
         section ([data-lrc-mp-subscription]), so there is no DOM row to read here. Prefer
         lineId match (stable; the items list re-sorts), fall back to index. */
      var bundle = window.LRCMP_SUBS && window.LRCMP_SUBS.contracts && window.LRCMP_SUBS.contracts[window.LRCMP_SUB_ACTIVE || 0];
      var lines = toArray(pick(bundle, ["contract.lines.nodes", "contract.lines"], []));
      var line = null, k;
      if (lineId) { for (k = 0; k < lines.length; k++) { if (String(pick(lines[k], ["id"], "")) === String(lineId)) { line = lines[k]; break; } } }
      if (!line && lines.length) line = lines[idx] || null;                        /* fallback by index */
      editCtx.lineId = line ? String(pick(line, ["id"], lineId || "")) : (lineId || "");
      editCtx.variantId = line ? String(pick(line, ["variantId"], "")) : "";
      var qty = line ? (parseInt(pick(line, ["quantity"], 1), 10) || 1) : 1;
      editCtx.origQty = qty; editCtx.qty = qty;
      editCtx.title = (line ? String(pick(line, ["title", "productTitle"], "")) : "") || "Item";
      var vt = line ? String(pick(line, ["variantTitle"], "")) : "";
      if (vt === "Default Title") vt = "";                                          /* Shopify default ⇒ not meaningful */
      var ey = region("edit-eyebrow"); if (ey) ey.textContent = siGet("data-si-edit-eyebrow", "EDIT ITEM");
      var t = region("edit-title"); if (t) t.textContent = editCtx.title;
      var sub = region("edit-subhead");
      if (sub) {
        if (vt) { sub.textContent = siGet("data-si-edit-subhead-format", "{variantTitle}").replace(/\{variantTitle\}/g, vt); sub.hidden = false; }
        else { sub.textContent = ""; sub.hidden = true; }
      }
      /* Hide the Swap button for a line carrying a reward/applied discount: discountAllocations non-empty, OR paid
         below its sub price (catches one-time $0 redeems + free gifts). Swapping would destroy a variant-specific
         discount the member redeemed. Member-tier-only lines keep swap (their discount is in pricingPolicy/currentPrice,
         NOT discountAllocations). Runs every open (no stale state); defaults to SHOWING swap if the line lookup failed. */
      var __hasReward = false;
      if (line) {
        var __qty = Math.max(1, parseInt(pick(line, ["quantity"], 1), 10) || 1);
        var __cur = moneyVal(pick(line, ["currentPrice"], null));
        var __ldT = moneyVal(pick(line, ["lineDiscountedPrice.amount", "lineDiscountedPrice"], null));
        var __paid = (__ldT != null) ? (__ldT / __qty) : moneyVal(pick(line, ["lineDiscountedPrice.pricePerUnit"], null));
        var __hasAlloc = toArray(pick(line, ["discountAllocations"], [])).length > 0;
        var __belowPrice = (__cur != null && __paid != null && __paid < __cur - 0.005);
        __hasReward = __hasAlloc || __belowPrice;
      }
      var __swapBtn = editEl("[data-edit-swap]");
      if (__swapBtn) { __swapBtn.hidden = __hasReward; __swapBtn.style.display = __hasReward ? "none" : ""; }
      setEditQty(qty); resetEditModal();
      LRCMP.open("editModal");
    },
    swap: function () {
      /* Close edit, mark the swap intent, open the add picker focused on the matching pillar.
         The add-commit handler removes swapMode.lineId after the new line is added. */
      LRCMP.close("editModal");
      LRCMP.swapMode = { lineId: editCtx.lineId, contractId: addContractId() };
      var tabId = resolveSwapPillar(editCtx.variantId);
      LRCMP.openAdd(tabId ? { tabId: tabId } : {});
    },
    openSurprise: function () { LRCMP.open("surpriseModal"); },
    openQuiz: function () { LRCMP.open("quizModal"); },
    openReschedule: function () {
      var t = rsToday();
      rsSel = null; rsScheduled = null;
      /* scheduled = contract's current nextBillingDate; open the view on its month (fallback: today). */
      var bundle = window.LRCMP_SUBS && window.LRCMP_SUBS.contracts && window.LRCMP_SUBS.contracts[window.LRCMP_SUB_ACTIVE || 0];
      var dto = (bundle && bundle.dto) || {};
      var iso = dto.nextBillingDate || dto.nextPaidBillingDate || (bundle && bundle.contract && bundle.contract.nextBillingDate) || null;
      var sd = iso ? new Date(iso) : null;
      if (sd && !isNaN(sd.getTime())) {
        rsScheduled = { y: sd.getFullYear(), m: sd.getMonth(), d: sd.getDate() };
        rsView = (rsMonthIdx(rsScheduled.y, rsScheduled.m) < rsMonthIdx(t.y, t.m))   /* past ⇒ today's month */
          ? { y: t.y, m: t.m } : { y: rsScheduled.y, m: rsScheduled.m };
      } else { rsView = { y: t.y, m: t.m }; }
      var ey = region("rs-eyebrow"); if (ey) ey.textContent = rsGet("data-rs-eyebrow", "RESCHEDULE");
      var hd = region("rs-headline"); if (hd) hd.textContent = rsGet("data-rs-headline", "Pick a new ship date");
      var sh = region("rs-subhead"); if (sh) sh.textContent = rsGet("data-rs-subhead", "Move your next shipment. Nothing else changes.");
      var sc = region("rs-softcta"); if (sc) sc.textContent = rsGet("data-rs-softcta", "↺ Help me find the right frequency instead");
      var lt = region("rs-legend-today"); if (lt) lt.textContent = rsGet("data-rs-legend-today", "Today");
      var ld = region("rs-legend-scheduled"); if (ld) ld.textContent = rsGet("data-rs-legend-scheduled", "Next ship");
      var ls = region("rs-legend-selected"); if (ls) ls.textContent = rsGet("data-rs-legend-selected", "Selected");
      renderCal(rsView.y, rsView.m); rsUpdateCta();
      LRCMP.open("rescheduleModal");
    },
    openCadence: function () {
      var bundle = window.LRCMP_SUBS && window.LRCMP_SUBS.contracts && window.LRCMP_SUBS.contracts[window.LRCMP_SUB_ACTIVE || 0];
      var dto = (bundle && bundle.dto) || {};
      var cur = num(dto.deliveryPolicyIntervalCount || dto.billingPolicyIntervalCount || (bundle && bundle.contract && bundle.contract.billingPolicy && bundle.contract.billingPolicy.intervalCount));
      cadState = { picked: null, current: (cur != null ? cur : null) };
      var ey = region("cad-eyebrow"); if (ey) ey.textContent = cadGet("data-cad-eyebrow", "DELIVERY FREQUENCY");
      var hd = region("cad-headline"); if (hd) hd.textContent = cadGet("data-cad-headline", "How often?");
      var sh = region("cad-subhead"); if (sh) sh.textContent = cadGet("data-cad-subhead", "Your recurring cadence. We'll fine-tune this with the usage quiz.");
      var sc = region("cad-softcta"); if (sc) sc.textContent = cadGet("data-cad-softcta", "✦ Not sure? Take the usage quiz");
      var m = modal("cadenceModal"), cards = m ? m.querySelectorAll("[data-cad-days]") : [];
      Array.prototype.forEach.call(cards, function (cd) {                          /* pre-select the card matching current cadence */
        if (cur != null && num(cd.getAttribute("data-cad-days")) === cur) { cd.classList.add("sel"); cadState.picked = cur; }
        else cd.classList.remove("sel");
      });
      cadUpdateCta();
      LRCMP.open("cadenceModal");
    },
    openGoal: function () { LRCMP.open("goalModal"); },
    openAddr: function () {
      var bundle = window.LRCMP_SUBS && window.LRCMP_SUBS.contracts && window.LRCMP_SUBS.contracts[window.LRCMP_SUB_ACTIVE || 0];
      var contract = (bundle && bundle.contract) || {}, dto = (bundle && bundle.dto) || {};
      var a = (contract.deliveryMethod && contract.deliveryMethod.address) || dto.shippingAddress || {};
      var ey = region("adr-eyebrow"); if (ey) ey.textContent = adrGet("data-adr-eyebrow", "SHIPPING ADDRESS");
      var hd = region("adr-headline"); if (hd) hd.textContent = adrGet("data-adr-headline", "Where it ships");
      var sh = region("adr-subhead"); if (sh) sh.textContent = adrGet("data-adr-subhead", "Update where your subscription ships. Takes effect on your next order.");
      var aa = region("adr-applyall"); if (aa) aa.textContent = adrGet("data-adr-applyall", "Apply to all my subscriptions");
      adrClearErrors();
      var first = a.firstName || "", last = a.lastName || "";
      if (!first && !last && a.name) { var parts = String(a.name).trim().split(/\s+/); first = parts.shift() || ""; last = parts.join(" "); }   /* split legacy single "name" */
      adrSetVal("firstName", first); adrSetVal("lastName", last);
      adrSetVal("address1", a.address1); adrSetVal("address2", a.address2); adrSetVal("city", a.city);
      adrSetVal("zip", a.zip); adrSetVal("phone", a.phone);
      var cc = String(a.countryCode || "").toUpperCase();
      if (!ADDR_COUNTRIES.some(function (c) { return c.code === cc; })) cc = "US";                 /* default US if absent/unsupported */
      var csel = adrField("countryCode"); if (csel) csel.innerHTML = adrOptions(ADDR_COUNTRIES, cc);
      adrRenderProvinces(cc, String(a.provinceCode || "").toUpperCase());
      var chk = adrModalQ("#adrApplyAll"); if (chk) chk.checked = true;
      var cta = adrModalQ("[data-adr-cta]"); if (cta) { cta.disabled = false; delete cta.dataset.idle; cta.textContent = adrGet("data-adr-cta-idle", "Save address"); }
      LRCMP.open("addrModal");
    },
    openPay: function () {
      var bundle = window.LRCMP_SUBS && window.LRCMP_SUBS.contracts && window.LRCMP_SUBS.contracts[window.LRCMP_SUB_ACTIVE || 0];
      var pay = (bundle && bundle.payment) || {};
      var ey = region("pay-eyebrow"); if (ey) ey.textContent = payGet("data-pay-eyebrow", "PAYMENT METHOD");
      var hd = region("pay-headline"); if (hd) hd.textContent = payGet("data-pay-headline", "Your saved card");
      var sh = region("pay-subhead"); if (sh) sh.textContent = payGet("data-pay-subhead", "Update your card via a secure link sent to your email.");
      var card = payRegion("pay-methods");
      if (card) {
        var last4 = pay.cardLastDigits;
        if (last4) {
          /* brand resolution (Shop Pay agreements expose no cardBrand): cardBrand → Shop Pay → generic */
          var brand;
          if (pay.cardBrand) brand = payCap(pay.cardBrand);
          else if (pay.instrumentType === "CustomerShopPayAgreement") brand = payGet("data-pay-shoppay-label", "Shop Pay");
          else brand = payGet("data-pay-card-label", "Card");
          var mm = String(pay.cardExpiryMonth == null ? "" : pay.cardExpiryMonth); if (mm && mm.length < 2) mm = "0" + mm;
          card.textContent = payGet("data-pay-current-template", "{brand} ending in {last4} · exp {exp_month}/{exp_year}")
            .replace(/\{brand\}/g, brand).replace(/\{last4\}/g, last4)
            .replace(/\{exp_month\}/g, mm).replace(/\{exp_year\}/g, String(pay.cardExpiryYear == null ? "" : pay.cardExpiryYear));
        } else { card.textContent = payGet("data-pay-no-card", "No card on file"); }
      }
      payReset();
      LRCMP.open("payModal");
    },
    openPrepay: function () { if (LRCMP.openStash) { LRCMP.openStash(); } else { LRCMP.open("prepayModal"); } },
    openPause: function () { LRCMP.open("pauseModal"); },
    openCancel: function () { LRCMP.goStep(1); LRCMP.open("cancelModal"); },
    pick: function (el) { Array.prototype.forEach.call(el.parentElement.querySelectorAll(".qopt"), function (o) { o.classList.remove("sel"); }); el.classList.add("sel"); },
    pickFrq: function (el) {
      Array.prototype.forEach.call(el.parentElement.querySelectorAll(".frq"), function (o) { o.classList.remove("sel"); });
      el.classList.add("sel");
      cadState.picked = num(el.getAttribute("data-cad-days"));
      cadUpdateCta();
    },
    quizPick: function (el) { LRCMP.pick(el); var q = region("quiz-result"); if (q) q.classList.add("show"); },
    pickCause: function (el) { Array.prototype.forEach.call(root.querySelectorAll(".cause"), function (c) { c.classList.remove("sel"); }); el.classList.add("sel"); },
    goalPick: function (el, type) { LRCMP.pick(el); var g = region("goal-date"); if (g) g.style.display = (type === "event") ? "block" : "none"; },
    rateChange: function (v) {
      var m = region("ratemsg"); if (!m) return; v = +v;
      if (v <= 2) { m.className = "hratemsg low"; m.innerHTML = "Sorry that one missed — we've noted it."; }
      else { m.className = "hratemsg high"; m.innerHTML = "Love that. <b>Leave a quick review and earn points.</b>"; }
    },
    cancelReason: function (r) {
      var SAVES = {
        much:   { t: "Sounds like timing, not coffee. Try a rung down first:", opts: [["🗓","Right-size your cadence","We stop the pile-up"],["📦","Smaller bag or lower quantity","Less per shipment, same price"],["⏯","Pause for a month","Keep points & perks"]] },
        cost:   { t: "Let's make it fit your budget:", opts: [["◆","Prepay & save more","Lower effective price"],["🗓","Stretch the cadence","Fewer orders, same %"],["📦","Step down a size","Lighter bill"]] },
        flavor: { t: "That's on us — let's fix the cup:", opts: [["🎁","Try another flavor","A different blend next shipment"],["⇄","Swap your blend","Switch to something better"],["↻","Let us surprise you","Tell us your taste"]] },
        break:  { t: "No need to cancel for a breather:", opts: [["⏯","Pause — pick a duration","Resumes automatically"],["🗓","Push your next shipment out","Skip without ending"]] }
      };
      var s2 = SAVES[r] || SAVES.break;
      var tEl = region("save-title"); if (tEl) tEl.textContent = s2.t;
      var oEl = region("save-opts");
      if (oEl) oEl.innerHTML = s2.opts.map(function (o, i) {
        return '<div class="save' + (i === 0 ? " hi" : "") + '" onclick="LRCMP.noop(event)"><div class="si">' + o[0] + '</div><div><div class="sn">' + esc(o[1]) + '</div><div class="sd">' + esc(o[2]) + "</div></div></div>";
      }).join("");
      LRCMP.goStep(2);
    },
    goStep: function (n) { Array.prototype.forEach.call(root.querySelectorAll('[data-step]'), function (el) { el.classList.toggle("on", +el.getAttribute("data-step") === n); }); },
    startTour: startTour, tourNext: tourNext, tourBack: tourBack, endTour: endTour
  };
  window.LRCMP = LRCMP;

  /* ---------- addModal: add-to-subscription commit (writes phase) ----------
     Lives here (not the section) so it owns the modal's render + click path; the
     section passes copy via data-mono-add-* on root. openAdd(product) focuses the
     modal on the passed product and injects a REAL commit button (vs the inert sample
     Add buttons). No product ⇒ default modal content is left untouched. */
  var MONO_ADD_WORKING = root.getAttribute("data-mono-add-working") || "Working...";
  var MONO_ADD_ERROR = root.getAttribute("data-mono-add-error") || "Something went wrong. Nothing was changed. Please try again.";
  var MONO_ADD_SUCCESS = root.getAttribute("data-mono-add-success") || "Added to your subscription! Refreshing...";
  function addContractId() {
    var c = window.LRCMP_SUBS && window.LRCMP_SUBS.contracts && window.LRCMP_SUBS.contracts[window.LRCMP_SUB_ACTIVE || 0];
    var d = (c && c.dto) || c || {};
    return String(d.subscriptionContractId || d.contractId || d.id || "");
  }
  /* Normalize a variant id (gid or bare) to its trailing numeric id for cross-source matching. */
  function vKey(x) { x = String(x == null ? "" : x); var m = x.match(/(\d+)\s*$/); return m ? m[1] : x; }
  /* Find the add-module pillar whose catalog contains the given variant; return its blockId (tabId) or null. */
  function resolveSwapPillar(variantId) {
    var key = vKey(variantId); if (!key) return null;
    var am = window.LRCMP_ADD_MODULE, pillars = (am && am.pillars) || [];
    for (var i = 0; i < pillars.length; i++) {
      var prods = (pillars[i] && pillars[i].products) || [];
      for (var j = 0; j < prods.length; j++) {
        var vs = (prods[j] && prods[j].variants) || [];
        for (var k = 0; k < vs.length; k++) { if (vs[k] && vKey(vs[k].id) === key) return String(pillars[i].blockId); }
      }
    }
    return null;                                                /* no match ⇒ openAdd defaults to first tab */
  }
  /* arg may be a single product {handle,variantId,title}, a roster array, or nothing (read the shipment quick-add roster) */
  function resolveRoster(arg) {
    if (Array.isArray(arg)) return arg;
    if (arg && arg.variantId) return [arg];                     /* single product (e.g. the stack CTA) */
    var sr = document.querySelector("[data-lrc-mp-shipment-items]");
    if (sr) { try { return JSON.parse(sr.getAttribute("data-si-quick-add") || "[]"); } catch (e) {} }
    return [];
  }
  function renderAddFocus(roster) {
    var reg = region("add-products"); if (!reg) return;
    var items = (roster || []).filter(function (p) { return p && p.variantId; });
    if (!items.length) return;                                  /* nothing resolves ⇒ leave default modal content */
    reg.innerHTML = items.map(function (p) {
      var vid = String(p.variantId), title = p.title || "this product";
      var thumb = p.image ? '<div class="thumb"><img src="' + esc(p.image) + '" alt="" loading="lazy"></div>' : "";
      return '<div class="ptile">' + thumb + '<div class="pn">' + esc(title) + "</div>" +
        '<div data-add-err hidden style="margin:8px 0 0;font-size:12px;font-weight:600;color:#BC5530"></div>' +
        '<button type="button" class="btn btn-primary btn-sm btn-block" data-add-commit data-variant-id="' + esc(vid) + '" data-qty="1">Add to my subscription</button></div>';
    }).join("");
  }
  function addErr(btn) {
    btn.disabled = false;
    if (btn.dataset.prevLabel != null) { btn.textContent = btn.dataset.prevLabel; delete btn.dataset.prevLabel; }
    var e = btn.parentNode && btn.parentNode.querySelector("[data-add-err]"); if (e) { e.textContent = MONO_ADD_ERROR; e.hidden = false; }
  }
  document.addEventListener("click", function (ev) {
    var btn = ev.target.closest && ev.target.closest("[data-add-commit]"); if (!btn || btn.disabled) return;
    ev.preventDefault();
    var e = btn.parentNode && btn.parentNode.querySelector("[data-add-err]"); if (e) e.hidden = true;
    var vid = btn.getAttribute("data-variant-id") || "", qty = btn.getAttribute("data-qty") || "1", cid = addContractId();
    if (!vid || !cid) { addErr(btn); return; }
    if (btn.dataset.prevLabel == null) btn.dataset.prevLabel = btn.textContent;
    btn.disabled = true; btn.textContent = MONO_ADD_WORKING;
    var url = "/apps/subscriptions/cp/api/v2/subscription-contracts-add-line-item?contractId=" + encodeURIComponent(cid) +
      "&variantId=" + encodeURIComponent(vid) + "&quantity=" + encodeURIComponent(qty) + "&isOneTimeProduct=false";
    fetch(url, { method: "PUT", credentials: "same-origin", redirect: "manual", headers: { "Accept": "application/json" } })
      .then(async function (r) { if (!r.ok || r.type === "opaqueredirect" || r.status === 0) throw new Error("add_failed"); btn.textContent = MONO_ADD_SUCCESS; await (window.LRCMP_REFRESH_AFTER_CHANGE ? window.LRCMP_REFRESH_AFTER_CHANGE({ immediate: true }) : location.reload()); })
      .catch(function () { addErr(btn); });
  });

  /* ============================================================
     ADD MODULE — tabbed pillared product picker (LRCMP.openAdd)
     Reads window.LRCMP_ADD_MODULE (emitted by sections/lrc-mp-add-module.liquid).
     ============================================================ */
  var AM = { data: null, active: 0, mode: "subscribe", busy: false };
  function amModalEl() { return modal("addModal"); }
  function amQ(sel) { var m = amModalEl(); return m ? m.querySelector(sel) : null; }
  function amMoney(c) { if (c == null) return ""; var n = Number(c); if (isNaN(n)) return ""; return "$" + (n / 100).toFixed(2); }
  function amExplodeKey(product, explodeOn) {
    explodeOn = String(explodeOn || "").trim();
    if (/^option[123]$/i.test(explodeOn)) return explodeOn.toLowerCase();
    if (!explodeOn) return null;
    var opts = product.options || [];
    for (var i = 0; i < opts.length; i++) { if (String(opts[i]).toLowerCase() === explodeOn.toLowerCase()) return "option" + (i + 1); }
    return null;                                                 /* explode option not present ⇒ one tile per product */
  }
  function amGroups(product, explodeOn) {
    var key = amExplodeKey(product, explodeOn), vs = (product.variants || []).filter(function (v) { return v.available; });   /* fix 2: hide OOS variants entirely */
    if (!key) return vs.length ? [{ value: null, key: null, variants: vs }] : [];
    var map = {}, order = [];
    vs.forEach(function (v) { var val = v[key], k = (val == null ? "__none" : String(val)); if (!map[k]) { map[k] = { value: val, key: key, variants: [] }; order.push(k); } map[k].variants.push(v); });
    return order.map(function (k) { return map[k]; });
  }
  function amRemainingLabel(v, key) {
    var out = []; ["option1", "option2", "option3"].forEach(function (ok) { if (ok !== key && v[ok] != null && v[ok] !== "") out.push(v[ok]); });
    return out.join(" / ") || v.title || "Default";
  }
  function amSubscribable(group) { return group.variants.some(function (v) { return v.subscribable === true; }); }   /* non-subscribable group = every variant subscribable===false */
  function amFirstAvailable(vs) { for (var i = 0; i < vs.length; i++) { if (vs[i].available) return vs[i]; } return vs[0] || null; }
  /* One-time adds now ship at FULL price, so the preview is mode-dependent:
       • subscribe mode + subscribable (subscriberPrice present) ⇒ <s>{price}</s> {subscriberPrice}
         (the recurring member discount; strike only when price > subscriberPrice)
       • subscribe mode + non-subscribable (gear), OR one-time mode (any variant) ⇒ full price,
         with a compare-at strikethrough only when actually on sale.  (all values in cents) */
  /* Tier-aware subscriber price for a variant: the customer's matched-plan allocation price,
     else the Liquid-emitted min (subscriberPrice) so there's no regression. */
  function amSubFor(v) {
    var fb = (v.subscriberPrice == null || v.subscriberPrice === "") ? null : v.subscriberPrice;
    var pid = window.lrcCustomerPlanId ? window.lrcCustomerPlanId() : null;
    var allocs = v.allocations;
    if (pid && allocs && allocs.length) { for (var i = 0; i < allocs.length; i++) { if (String(allocs[i].id) === pid) return allocs[i].price; } }
    return fb;
  }
  function amPriceFor(price, compareAt, sub, mode) {
    var p = num(price);
    var s = (sub != null && sub !== "") ? num(sub) : null;       /* subscriberPrice ⇒ variant is subscribable */
    var ca = (compareAt != null && compareAt !== "") ? num(compareAt) : null;
    if (mode === "subscribe" && s != null) { return ((s < p) ? "<s>" + amMoney(p) + "</s> " : "") + amMoney(s); }
    return ((ca != null && ca > p) ? "<s>" + amMoney(ca) + "</s> " : "") + amMoney(p);
  }

  function amOpen(opts) {
    AM.data = window.LRCMP_ADD_MODULE || { copy: {}, pillars: [] };
    AM.mode = "subscribe";
    var pillars = AM.data.pillars || [], idx = 0;
    var tabId = (opts && typeof opts === "object" && opts.tabId) ? String(opts.tabId) : null;   /* legacy non-object args ignored */
    if (tabId) { for (var i = 0; i < pillars.length; i++) { if (String(pillars[i].blockId) === tabId) { idx = i; break; } } }
    AM.active = idx;
    amRenderChrome(); amRenderTabs(); amRenderGrid();
    LRCMP.open("addModal");
    /* one-time amber shimmer across the tab bar on each open: remove + reflow + re-add the class so the 1-iteration
       animation restarts every time (the host persists between opens). Skip when the bar is hidden (<=1 pillar). */
    var tabsEl = amQ("[data-am-tabs]");
    if (tabsEl && tabsEl.style.display !== "none") { tabsEl.classList.remove("lrcam-shimmer"); void tabsEl.offsetWidth; tabsEl.classList.add("lrcam-shimmer"); }
  }
  /* INTERIM GUARD: one-time adds ship on EVERY delivery of a prepaid term (4× on 12mo Stash) — no per-line
     single-delivery primitive exists yet, so block one-time on prepaid contracts. Detection = >1 delivery per
     billing cycle (the true bug condition) OR an explicit prepaid Stash plan id (6mo/12mo) on a line.
     NOTE: 692168458553 (25% Tier 90 Days) is 90/90 PAYG → not blocked by id; the interval test covers it if ever prepaid. */
  function amActiveIsPrepaid() {
    var b = window.LRCMP_SUBS && window.LRCMP_SUBS.contracts && window.LRCMP_SUBS.contracts[window.LRCMP_SUB_ACTIVE || 0];
    if (!b) return false;
    var d = b.dto || {}, c = b.contract || {};
    var bI = String((c.billingPolicy && c.billingPolicy.interval) || d.billingPolicyInterval || "").toUpperCase();
    var dI = String((c.deliveryPolicy && c.deliveryPolicy.interval) || d.deliveryPolicyInterval || "").toUpperCase();
    var bC = num((c.billingPolicy && c.billingPolicy.intervalCount != null) ? c.billingPolicy.intervalCount : d.billingPolicyIntervalCount);
    var dC = num((c.deliveryPolicy && c.deliveryPolicy.intervalCount != null) ? c.deliveryPolicy.intervalCount : d.deliveryPolicyIntervalCount);
    if (bI && dI && bI === dI && bC > 0 && dC > 0 && bC > dC) return true;   /* >1 delivery per billing cycle */
    var lines = (c.lines && (c.lines.nodes || c.lines)) || [];
    var STASH = { "702230561081": 1, "702230593849": 1 };   /* 6mo + 12mo prepaid Stash plans */
    for (var i = 0; i < lines.length; i++) { var sp = String((lines[i] && lines[i].sellingPlanId) || "").replace(/\D/g, ""); if (STASH[sp]) return true; }
    return false;
  }
  /* Cross-section read: the rewards shelf gates one-time add-to-order on the active contract being prepaid. */
  window.LRCMP_IS_ACTIVE_PREPAID = function () { try { return !!amActiveIsPrepaid(); } catch (e) { return false; } };
  /* On prepaid (Stash) contracts, one-time items can't be added to the contract (they'd ship every delivery).
     Instead of a toggle we show a single info callout box pointing to the storefront for one-time purchases. */
  function prepaidCalloutCfg() {
    var s = document.querySelector("[data-lrc-mp-subscription]");
    function g(name, dflt) { var v = s && s.getAttribute(name); return (v && v.trim()) || dflt; }
    return {
      heading: g("data-prepaid-onetime-heading", "Looking for one-time items?"),
      body: g("data-prepaid-onetime-body", "Add one-time products like gear or limited flavors through the regular shop. They'll ship independently from your subscription."),
      label: g("data-prepaid-onetime-cta-label", "Browse the shop →"),
      url: g("data-prepaid-onetime-cta-url", "/collections/all")
    };
  }
  function amSetPrepaidCallout(show) {
    var tg = amQ("[data-am-toggle]"); if (!tg) return;
    var host = tg.parentNode || tg;
    var el = host.querySelector("[data-am-prepaid-callout]");
    if (show) {
      var cfg = prepaidCalloutCfg();
      if (!el) {
        el = document.createElement("div"); el.setAttribute("data-am-prepaid-callout", ""); el.className = "am-prepaid-callout";
        /* muted info panel — matches the portal's savings-anchor / cad-locked treatment (navy-tint bg, subtle border) */
        el.style.cssText = "margin:12px 0;padding:14px 16px;background:rgba(34,63,78,0.06);border:1px solid rgba(34,63,78,0.18);border-radius:12px;";
        host.insertBefore(el, tg.nextSibling);
      }
      el.innerHTML =
        '<div style="font-weight:700;font-size:14px;color:#223F4E;">' + esc(cfg.heading) + '</div>' +
        '<div style="margin-top:6px;font-size:13px;line-height:1.5;color:rgba(34,63,78,0.78);">' + esc(cfg.body) + '</div>' +
        '<a href="' + esc(cfg.url) + '" style="display:inline-block;margin-top:10px;font-weight:600;font-size:13px;color:#223F4E;text-decoration:underline;text-underline-offset:3px;">' + esc(cfg.label) + '</a>';
      el.hidden = false;
    } else if (el) { el.hidden = true; }
  }
  function amRenderChrome() {
    var c = (AM.data && AM.data.copy) || {};
    var ey = amQ("[data-am-eyebrow]"); if (ey) ey.textContent = c.eyebrow || "";
    var hd = amQ("[data-am-headline]"); if (hd) hd.textContent = c.headline || "";
    var prepaid = amActiveIsPrepaid();
    if (prepaid) AM.mode = "subscribe";   /* one-time disabled on prepaid → force subscribe mode */
    var tg = amQ("[data-am-toggle]");
    if (tg) {
      if (prepaid) {
        tg.innerHTML = ""; tg.style.display = "none";   /* no toggle on prepaid (recurring-only); callout replaces it */
      } else {
        tg.style.display = "";
        tg.innerHTML = '<button type="button" data-am-mode="subscribe" class="' + (AM.mode === "subscribe" ? "on" : "") + '">' + esc(c.subscribeToggle || "Subscribe") + "</button>" +
          '<button type="button" data-am-mode="onetime" class="' + (AM.mode === "onetime" ? "on" : "") + '">' + esc(c.onetimeToggle || "One-time") + "</button>";
      }
    }
    amSetPrepaidCallout(prepaid);
  }
  function amRenderTabs() {
    var host = amQ("[data-am-tabs]"); if (!host) return;
    var pillars = (AM.data && AM.data.pillars) || [];
    host.style.display = (pillars.length <= 1) ? "none" : "";
    host.innerHTML = pillars.map(function (p, i) { return '<button type="button" class="lrcam-tab' + (i === AM.active ? " on" : "") + '" data-am-tab="' + i + '">' + esc(p.title || ("Tab " + (i + 1))) + "</button>"; }).join("");
  }
  function amRenderGrid() {
    var host = amQ("[data-am-grid]"); if (!host) return;
    var c = (AM.data && AM.data.copy) || {};
    var pillar = ((AM.data && AM.data.pillars) || [])[AM.active];
    if (!pillar || !(pillar.products || []).length) { host.innerHTML = '<div class="lrcam-empty">' + esc(c.empty || "") + "</div>"; return; }
    var html = "";
    pillar.products.forEach(function (prod) {
      amGroups(prod, pillar.explodeOn).forEach(function (group) { html += amTile(prod, group, pillar); });
    });
    host.innerHTML = html || '<div class="lrcam-empty">' + esc(c.empty || "") + "</div>";
  }
  function amSelector(mode, group, prod, def) {
    if (mode === "pack_metafield") {
      var packs = prod.metafieldPackOptions;
      if (packs && packs.length) {
        return '<select data-am-pack>' + packs.map(function (p, i) { var q = num(p.qty) || 1, lbl = p.label || (q + " pack"); if (p.badge) lbl += " · " + p.badge; return '<option value="' + i + '" data-qty="' + q + '"' + (p.default ? " selected" : "") + ">" + esc(lbl) + "</option>"; }).join("") + "</select>";
      }
      mode = "quantity_stepper";                                 /* empty/missing metafield ⇒ graceful fallback */
    }
    if (mode === "native_variants") {
      return '<select data-am-variant>' + group.variants.map(function (v) {
        return '<option value="' + v.id + '" data-img="' + esc(v.featured_image || "") + '" data-price="' + esc(v.price) + '" data-compare="' + esc(v.compare_at_price == null ? "" : v.compare_at_price) + '" data-sub="' + esc(amSubFor(v) == null ? "" : amSubFor(v)) + '"' + (v === def ? " selected" : "") + ">" + esc(amRemainingLabel(v, group.key)) + "</option>"; }).join("") + "</select>";
    }
    if (mode === "quantity_stepper") {
      return '<div class="lrcam-step" data-am-step><button type="button" data-am-dec aria-label="Decrease">−</button><span data-am-qval>1</span><button type="button" data-am-inc aria-label="Increase">+</button></div>';
    }
    return "";                                                   /* none */
  }
  function amTile(prod, group, pillar) {
    var sub = amSubscribable(group);
    if (!sub && pillar.nonSub === "hide_when_subscribe_mode" && AM.mode === "subscribe") return "";
    var def = amFirstAvailable(group.variants); if (!def) return "";
    var fallbackImg = prod.featured_image || "";
    var img = def.featured_image || fallbackImg;
    var thumb = img ? '<div class="lrcam-thumb"><img src="' + esc(img) + '" alt="" loading="lazy" data-am-img></div>' : '<div class="lrcam-thumb"></div>';
    var title = (group.value != null && group.value !== "") ? group.value : prod.title;   /* fix 3: exploded ⇒ option value only; else product title */
    var dim = (!sub && pillar.nonSub === "dim_with_pill" && AM.mode === "subscribe");   /* one-time mode ⇒ all tiles normal, no dim/pill */
    var pill = dim ? '<span class="lrcam-pill">' + esc(pillar.pillText || "One-time only") + "</span>" : "";
    return '<div class="lrcam-tile' + (dim ? " lrcam-tile--nonsub" : "") + '" data-am-tile data-variant="' + esc(def.id) + '" data-qty="1" data-nonsub="' + (sub ? "0" : "1") + '" data-fallback-img="' + esc(fallbackImg) + '">' +
      thumb + pill +
      '<div class="lrcam-tt">' + esc(title) + "</div>" +
      '<div class="lrcam-price" data-am-price>' + amPriceFor(def.price, def.compare_at_price, amSubFor(def), AM.mode) + "</div>" +
      amSelector(pillar.mode || "native_variants", group, prod, def) +
      '<div class="lrcam-err" data-am-err hidden></div>' +
      '<button type="button" class="lrcam-add" data-am-add>Add</button></div>';
  }
  function amErr(btn, errEl) {
    var c = (AM.data && AM.data.copy) || {};
    amSetBusy(false);   /* release the modal-wide lock on failure so the user can retry */
    btn.disabled = false; delete btn.dataset.busy; if (btn.dataset.prevLabel != null) { btn.textContent = btn.dataset.prevLabel; delete btn.dataset.prevLabel; }
    if (errEl) { errEl.textContent = c.error || "Couldn't add that. Nothing was changed."; errEl.hidden = false; }
  }
  function amSetBusy(on) {   /* modal-wide commit lock + visible cue (is-am-busy on the addModal root) */
    AM.busy = !!on;
    var m = amModalEl(); if (m) m.classList.toggle("is-am-busy", !!on);
  }
  function amCommit(btn) {
    var tile = btn.closest("[data-am-tile]"); if (!tile) return;
    if (AM.busy) return;            /* modal-wide guard: block a second tap (ANY tile) while a commit is in flight */
    if (btn.dataset.busy) return;   /* guard double-submit WITHOUT :disabled (which dims the button via CSS) */
    var c = (AM.data && AM.data.copy) || {};
    var errEl = tile.querySelector("[data-am-err]"); if (errEl) errEl.hidden = true;
    var vid = tile.getAttribute("data-variant") || "", cid = addContractId();
    /* pack_metafield: read the LIVE selected pack option (a default/untouched multi-pack never fires "change",
       so the tile data-qty can be stale at "1"). Stepper keeps data-qty synced; native_variants has no pack
       select and legitimately stays qty=1 with a distinct variant. */
    var packSel = tile.querySelector("[data-am-pack]"), qty;
    if (packSel) {
      var packOpt = packSel.options[packSel.selectedIndex];
      qty = num(packOpt && packOpt.getAttribute("data-qty")) || num(tile.getAttribute("data-qty")) || 1;
    } else {
      qty = num(tile.getAttribute("data-qty")) || 1;
    }
    /* Two outcomes only — one-time adds now ship at FULL price (no auto-discount):
         • subscribe toggle + subscribable variant → recurring line (isOneTimeProduct=false)
         • one-time toggle, OR a non-subscribable variant (gear can't recur) → one-time line (isOneTimeProduct=true) */
    var oneTime = (AM.mode === "onetime") || (tile.getAttribute("data-nonsub") === "1");
    if (oneTime && amActiveIsPrepaid()) {   /* block one-time (incl. non-subscribable variants) on prepaid — would ship every delivery; point to the storefront callout */
      amSetPrepaidCallout(true);
      if (errEl) { errEl.textContent = prepaidCalloutCfg().body; errEl.hidden = false; }
      return;
    }
    if (!vid || !cid) { amErr(btn, errEl); return; }
    if (btn.dataset.prevLabel == null) btn.dataset.prevLabel = btn.textContent;
    amSetBusy(true);   /* lock the whole modal the instant Add is pressed (covers the in-flight + background-refresh window) */
    btn.dataset.busy = "1"; btn.textContent = "Working...";   /* enabled-but-busy: no opacity dimming */
    var url = "/apps/subscriptions/cp/api/v2/subscription-contracts-add-line-item?contractId=" + encodeURIComponent(cid) +
      "&variantId=" + encodeURIComponent(vid) + "&quantity=" + encodeURIComponent(qty) + "&isOneTimeProduct=" + (oneTime ? "true" : "false");
    /* in-place refresh after add (drops the artificial 1800ms wait; refresh handles propagation), then close the picker */
    var reload = function () {
      /* Close the picker IMMEDIATELY on mutation success (add[+remove] resolved) and clear the lock — don't wait
         out the full change-detection poll. The refresh runs in the BACKGROUND; the items list updates a beat later. */
      if (window.LRCMP && LRCMP.close) LRCMP.close("addModal");   /* LRCMP.close clears AM.busy + is-am-busy + swapMode */
      if (window.LRCMP_REFRESH_AFTER_CHANGE) {
        Promise.resolve(window.LRCMP_REFRESH_AFTER_CHANGE()).catch(function () {});
      } else { try { location.reload(); } catch (e) {} }
    };
    fetch(url, { method: "PUT", credentials: "same-origin", redirect: "manual", headers: { "Accept": "application/json" } })
      .then(function (r) {
        if (!r.ok || r.type === "opaqueredirect" || r.status === 0) throw new Error("add_failed");
        btn.textContent = "Added!";
        setTimeout(function () {   /* revert to the original label on its own timer, regardless of refresh/modal-close */
          if (btn.dataset.prevLabel != null) { btn.textContent = btn.dataset.prevLabel; delete btn.dataset.prevLabel; }
          delete btn.dataset.busy;
        }, 2000);
        var sm = LRCMP.swapMode;
        if (sm && sm.lineId) {
          /* Swap: the new line is in — now remove the original. Failure must not block the user. */
          var rmUrl = "/apps/subscriptions/cp/api/subscription-contracts-remove-line-item?contractId=" + encodeURIComponent(sm.contractId || cid) +
            "&lineId=" + encodeURIComponent(sm.lineId) + "&removeDiscount=false";
          fetch(rmUrl, { method: "PUT", credentials: "same-origin", redirect: "manual", headers: { "Accept": "application/json" } })
            .then(function (rr) {
              if (!rr.ok || rr.type === "opaqueredirect" || rr.status === 0) { if (window.console) console.warn("[lrc-member-portal] swap remove failed for", sm.lineId); }
              else { LRCMP.swapMode = null; }
            })
            .catch(function () { if (window.console) console.warn("[lrc-member-portal] swap remove error for", sm.lineId); })
            .then(reload);                                              /* reload regardless — add already succeeded */
        } else {
          reload();
        }
      })
      .catch(function () { amErr(btn, errEl); });
  }
  (function attachAM() {
    var m = amModalEl(); if (!m) return;
    m.addEventListener("click", function (ev) {
      var t = ev.target; if (!t || !t.closest) return;
      var mode = t.closest("[data-am-mode]"); if (mode) { AM.mode = mode.getAttribute("data-am-mode"); amRenderChrome(); amRenderGrid(); return; }
      var tab = t.closest("[data-am-tab]"); if (tab) { AM.active = num(tab.getAttribute("data-am-tab")) || 0; amRenderTabs(); amRenderGrid(); return; }
      var step = t.closest("[data-am-dec], [data-am-inc]");
      if (step) { var tile = t.closest("[data-am-tile]"); if (tile) { var q = num(tile.getAttribute("data-qty")) || 1; q += t.closest("[data-am-inc]") ? 1 : -1; q = Math.max(1, Math.min(10, q)); tile.setAttribute("data-qty", q); var qv = tile.querySelector("[data-am-qval]"); if (qv) qv.textContent = q; } return; }
      var add = t.closest("[data-am-add]"); if (add && !add.disabled && !AM.busy) { ev.preventDefault(); amCommit(add); return; }
    });
    m.addEventListener("change", function (ev) {
      var t = ev.target; if (!t || !t.matches) return;
      var tile = t.closest && t.closest("[data-am-tile]"); if (!tile) return;
      if (t.matches("[data-am-variant]")) {
        tile.setAttribute("data-variant", t.value);
        var opt = t.options[t.selectedIndex];
        var im = tile.querySelector("[data-am-img]");                                          /* fix 5: swap image to the selected variant's */
        if (im) im.src = (opt && opt.getAttribute("data-img")) || tile.getAttribute("data-fallback-img") || "";
        var pr = tile.querySelector("[data-am-price]");                                        /* keep price in sync with the selected variant */
        if (pr && opt) { var dc = opt.getAttribute("data-compare"), ds = opt.getAttribute("data-sub"); pr.innerHTML = amPriceFor(num(opt.getAttribute("data-price")), (dc === "" || dc == null) ? null : num(dc), (ds === "" || ds == null) ? null : num(ds), AM.mode); }
        return;
      }
      if (t.matches("[data-am-pack]")) { var o = t.options[t.selectedIndex]; tile.setAttribute("data-qty", num(o && o.getAttribute("data-qty")) || 1); return; }
    });
  })();

  /* ---------- editModal: quantity update + remove (writes); copy via data-si-* on the shipment section root ---------- */
  function siGet(name, dflt) { var r = document.querySelector("[data-lrc-mp-shipment-items]"); var v = r && r.getAttribute(name); return (v != null && v !== "") ? v : dflt; }
  var editCtx = { lineId: "", variantId: "", origQty: 1, qty: 1, title: "" };
  function editEl(sel) { var m = modal("editModal"); return m ? m.querySelector(sel) : null; }
  function setEditQty(n) { n = Math.max(0, Math.round(n) || 0); editCtx.qty = n; var v = editEl("[data-edit-val]"); if (v) v.textContent = String(n); var dec = editEl("[data-edit-dec]"); if (dec) dec.disabled = (n <= 0); }   /* min 0 ⇒ Save-at-0 = remove */
  function showEditErr() { var e = editEl("[data-edit-err]"); if (e) { e.textContent = siGet("data-si-error", "Something went wrong. Nothing was changed. Please try again."); e.hidden = false; } }
  function restoreBtn(btn) { btn.disabled = false; if (btn.dataset.prevLabel != null) { btn.textContent = btn.dataset.prevLabel; delete btn.dataset.prevLabel; } }
  function resetEditModal() {
    var e = editEl("[data-edit-err]"); if (e) e.hidden = true;
    var s = editEl("[data-edit-save]"); if (s) restoreBtn(s);
    var w = editEl("[data-edit-remove-wrap]"); if (w) w.innerHTML = '<button type="button" class="cancel-link" style="margin-top:16px" data-edit-remove>Remove from subscription</button>';
  }
  /* onOk (optional): return TRUE to claim the post-save update — the deferred
     LRCMP_REFRESH_AFTER_CHANGE repaint is then skipped. Anything falsey falls back to the
     normal refresh, so a row can never be left stale. */
  function editWrite(btn, makeUrl, successName, workingLabel, onOk) {
    var cid = addContractId();
    if (!editCtx.lineId || !cid) { showEditErr(); return; }
    var e = editEl("[data-edit-err]"); if (e) e.hidden = true;
    if (btn.dataset.prevLabel == null) btn.dataset.prevLabel = btn.textContent;
    btn.disabled = true; btn.textContent = siGet("data-si-working", workingLabel || "Working...");
    fetch(makeUrl(cid), { method: "PUT", credentials: "same-origin", redirect: "manual", headers: { "Accept": "application/json" } })
      .then(async function (r) {
        if (!r.ok || r.type === "opaqueredirect" || r.status === 0) throw new Error("write_failed");
        btn.textContent = siGet(successName, "Updated");
        var handled = false;
        try { handled = (typeof onOk === "function") ? onOk() === true : false; } catch (e) { handled = false; }
        if (!handled) await (window.LRCMP_REFRESH_AFTER_CHANGE ? window.LRCMP_REFRESH_AFTER_CHANGE({ immediate: true }) : location.reload());
        if (window.LRCMP && LRCMP.close) LRCMP.close("editModal");
      })
      .catch(function () { restoreBtn(btn); showEditErr(); });   /* non-ok / redirect / thrown ⇒ inline error, button restored, NO reload */
  }
  /* Line REMOVAL routes through the BACKEND (action:remove_line) instead of the direct CP PUT, so the backend sets
     removeDiscount correctly and LOGS Appstle's real response on failure. hasDiscount:true is sent unconditionally:
     removeDiscount=true is a no-op on a line with no discount but is REQUIRED to remove a discounted/redeemed line
     (the old hardcoded removeDiscount=false was the bug); the gift-rollback path already uses true safely. Success/
     failure handling mirrors editWrite exactly (refresh + close on ok; restoreBtn + generic error otherwise). */
  function editRemove(btn) {
    var cid = addContractId();
    if (!editCtx.lineId || !cid) { showEditErr(); return; }
    var e = editEl("[data-edit-err]"); if (e) e.hidden = true;
    if (btn.dataset.prevLabel == null) btn.dataset.prevLabel = btn.textContent;
    btn.disabled = true; btn.textContent = siGet("data-si-working", "Removing…");
    fetch("/apps/lrc", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "remove_line", contractId: cid, lineId: editCtx.lineId, hasDiscount: true }) })
      .then(function (r) { return r.json().catch(function () { return null; }); })
      .then(async function (j) {
        if (!j || j.ok !== true) { restoreBtn(btn); showEditErr(); return; }   /* {ok:false} (Appstle rejected — backend logged it) ⇒ generic error, button restored, NO reload */
        btn.textContent = siGet("data-si-remove-success", "Updated");
        await (window.LRCMP_REFRESH_AFTER_CHANGE ? window.LRCMP_REFRESH_AFTER_CHANGE({ immediate: true }) : location.reload());
        if (window.LRCMP && LRCMP.close) LRCMP.close("editModal");
      })
      .catch(function () { restoreBtn(btn); showEditErr(); });
  }
  var emEl = modal("editModal");
  if (emEl) emEl.addEventListener("click", function (ev) {
    var t;
    if (ev.target.closest("[data-edit-dec]")) { setEditQty(editCtx.qty - 1); return; }
    if (ev.target.closest("[data-edit-inc]")) { setEditQty(editCtx.qty + 1); return; }
    if ((t = ev.target.closest("[data-edit-save]"))) {
      if (editCtx.qty === editCtx.origQty) { LRCMP.close("editModal"); return; }   /* unchanged ⇒ just close, no write */
      if (editCtx.qty === 0) {                                                     /* qty 0 ⇒ remove; the Save click IS the confirmation */
        editRemove(t);                                                            /* route removal through the backend (remove_line) */
        return;
      }
      /* Try-it-free line: its price is derivable on the page (FREE at 1, tier x (qty-1) beyond),
         so update the row directly and skip the deferred repaint that otherwise lands up to 1.8s
         after the modal closes. Any other line keeps the normal refresh. */
      var __tfEl = document.querySelector("[data-lrct-root]");
      var __tfVar = __tfEl ? String(__tfEl.getAttribute("data-lrct-gift-variant") || "").replace(/[^0-9]/g, "") : "";
      var __isTF = !!__tfVar && String(editCtx.variantId || "").replace(/[^0-9]/g, "") === __tfVar;
      var __qty = editCtx.qty, __lid = editCtx.lineId;
      editWrite(t, function (cid) { return "/apps/subscriptions/cp/api/subscription-contracts-update-line-item-quantity?contractId=" + encodeURIComponent(cid) + "&quantity=" + encodeURIComponent(editCtx.qty) + "&lineId=" + encodeURIComponent(editCtx.lineId); },
        "data-si-save-success", null,
        __isTF ? function () { return !!(window.LRCMP_TF_QTY_APPLY && window.LRCMP_TF_QTY_APPLY(__lid, __qty)); } : null);
      return;
    }
    if (ev.target.closest("[data-edit-remove]")) {                                 /* step 1: inline confirm */
      var w = editEl("[data-edit-remove-wrap]"); if (!w) return;
      w.innerHTML = '<div class="subhead" style="margin-top:16px"></div><div class="grid2" style="margin-top:8px"><button type="button" class="btn btn-primary btn-block" data-edit-remove-yes>Confirm</button><button type="button" class="btn btn-ghost btn-block" data-edit-remove-no>Keep it</button></div>';
      w.firstChild.textContent = siGet("data-si-remove-confirm", "Remove {title} from your subscription?").replace(/\{title\}/g, editCtx.title || "this item"); /* set as text — no HTML injection */
      return;
    }
    if (ev.target.closest("[data-edit-remove-no]")) { resetEditModal(); return; }  /* keep */
    if ((t = ev.target.closest("[data-edit-remove-yes]"))) {                       /* step 2: confirm remove */
      editRemove(t);                                                              /* route removal through the backend (remove_line) */
      return;
    }
  });

  /* close modal on scrim click */
  Array.prototype.forEach.call(root.querySelectorAll(".scrim"), function (sc) {
    sc.addEventListener("click", function (e) { if (e.target === sc) { sc.classList.remove("show"); if (sc.getAttribute("data-modal") === "addModal") LRCMP.swapMode = null; } });
  });

  /* calendar (current month; today highlighted; selection inert) */
  /* ---------- rescheduleModal: paginated month calendar + billing-date write ---------- */
  var rsView = null, rsSel = null, rsScheduled = null;               /* displayed month {y,m}; selected date {y,m,d}; contract's current next-ship {y,m,d} */
  function rsSameDay(a, b) { return !!a && !!b && a.y === b.y && a.m === b.m && a.d === b.d; }
  function rsGet(name, dflt) { var r = document.querySelector("[data-lrc-mp-subscription]"); var v = r && r.getAttribute(name); return (v != null && v !== "") ? v : dflt; }
  function rsModalQ(sel) { var m = modal("rescheduleModal"); return m ? m.querySelector(sel) : null; }
  function rsToday() { var n = new Date(); return { y: n.getFullYear(), m: n.getMonth(), d: n.getDate() }; }
  function rsMonthIdx(y, m) { return y * 12 + m; }
  function rsFmtSel(s) { var dt = new Date(s.y, s.m, s.d); return DOW[dt.getDay()] + ", " + MON[s.m] + " " + s.d; }   /* "Sat, Jul 18" */
  function renderCal(y, m) {
    rsView = { y: y, m: m };
    var g = region("cal-grid"); if (!g) return;
    var head = region("cal-head"); if (head) head.textContent = MON_FULL[m] + " " + y;
    var t = rsToday(), isThisMonth = (y === t.y && m === t.m);
    var firstDow = new Date(y, m, 1).getDay(), daysInMonth = new Date(y, m + 1, 0).getDate();
    var html = ""; for (var b = 0; b < firstDow; b++) html += "<div></div>";
    for (var d = 1; d <= daysInMonth; d++) {
      var cls = "cal-day", tag = "", past = (isThisMonth && d < t.d);
      if (past) cls += " past";
      if (isThisMonth && d === t.d) { cls += " today"; tag = '<span class="cal-tag">today</span>'; }
      if (rsScheduled && rsScheduled.y === y && rsScheduled.m === m && rsScheduled.d === d) { cls += " scheduled"; tag = '<span class="cal-tag next">next</span>'; }
      if (rsSel && rsSel.y === y && rsSel.m === m && rsSel.d === d) cls += " sel";
      html += '<div class="' + cls + '"' + (past ? "" : ' onclick="LRCMP.selDate(' + y + "," + m + "," + d + ')"') + ">" + d + tag + "</div>";
    }
    g.innerHTML = html;
    var prev = rsModalQ("[data-rs-prev]"), next = rsModalQ("[data-rs-next]");
    if (prev) prev.disabled = (rsMonthIdx(y, m) <= rsMonthIdx(t.y, t.m));        /* can't go before this month */
    if (next) next.disabled = (rsMonthIdx(y, m) >= rsMonthIdx(t.y, t.m) + 12);   /* cap +12 months */
  }
  function rsShiftMonth(delta) {
    if (!rsView) return;
    var idx = rsView.y * 12 + rsView.m + delta;
    renderCal(Math.floor(idx / 12), ((idx % 12) + 12) % 12);
  }
  function rsUpdateCta() {
    var cta = rsModalQ("[data-rs-cta]"); if (!cta) return;
    /* enabled only when a date is picked AND it isn't the already-scheduled date (that'd be a no-op) */
    if (rsSel && !rsSameDay(rsSel, rsScheduled)) { cta.disabled = false; cta.textContent = rsGet("data-rs-cta-template", "Move my shipment to {date}").replace(/\{date\}/g, rsFmtSel(rsSel)); }
    else { cta.disabled = true; cta.textContent = rsGet("data-rs-cta-idle", "Pick a date"); }
  }
  LRCMP.selDate = function (y, m, d) {
    rsSel = { y: y, m: m, d: d };
    renderCal(rsView ? rsView.y : y, rsView ? rsView.m : m);   /* re-render to paint .sel; preserves view */
    rsUpdateCta();
  };
  function rsCommit(cta) {
    if (!rsSel) return;                                                          /* defensive — button should be disabled */
    var bundle = window.LRCMP_SUBS && window.LRCMP_SUBS.contracts && window.LRCMP_SUBS.contracts[window.LRCMP_SUB_ACTIVE || 0];
    var dto = (bundle && bundle.dto) || {};
    var cid = String(dto.subscriptionContractId || dto.contractId || dto.id || "");
    var selFmt = rsFmtSel(rsSel);
    if (!cid) { cta.textContent = rsGet("data-rs-error", "Couldn't update shipment date. Nothing was changed."); return; }
    var iso = new Date(rsSel.y, rsSel.m, rsSel.d, 12, 0, 0).toISOString();        /* noon-local guard against TZ rollover */
    cta.disabled = true; cta.textContent = rsGet("data-rs-working", "Updating…");
    var url = "/apps/subscriptions/cp/api/subscription-contracts-update-billing-date?contractId=" + encodeURIComponent(cid) +
      "&nextBillingDate=" + encodeURIComponent(iso) + "&rescheduleFutureOrder=true";
    fetch(url, { method: "PUT", credentials: "same-origin", redirect: "manual", headers: { "Accept": "application/json" } })
      .then(function (r) {
        if (!r.ok || r.type === "opaqueredirect" || r.status === 0) {
          if ((r.status === 401 || r.status === 404) && window.console) console.warn("[lrc-member-portal] reschedule: cp endpoint returned " + r.status + " — cp proxy may not serve update-billing-date; backend fallback needed.");
          throw new Error("reschedule_failed_" + r.status);
        }
        cta.textContent = rsGet("data-rs-success-template", "Shipment moved to {date}").replace(/\{date\}/g, selFmt);
        setTimeout(function () { try { location.reload(); } catch (e) {} }, 1800);
      })
      .catch(function (err) {
        if (window.console) console.warn("[lrc-member-portal] reschedule failed:", err && err.message);
        cta.textContent = rsGet("data-rs-error", "Couldn't update shipment date. Nothing was changed.");
        setTimeout(function () { rsUpdateCta(); }, 2500);                          /* revert to actionable state so the user can retry */
      });
  }
  (function attachReschedule() {
    var m = modal("rescheduleModal"); if (!m) return;
    m.addEventListener("click", function (ev) {
      var t = ev.target; if (!t || !t.closest) return;
      var prev = t.closest("[data-rs-prev]"); if (prev) { if (!prev.disabled) rsShiftMonth(-1); return; }
      var next = t.closest("[data-rs-next]"); if (next) { if (!next.disabled) rsShiftMonth(1); return; }
      var cta = t.closest("[data-rs-cta]"); if (cta && !cta.disabled) { rsCommit(cta); return; }
    });
  })();

  /* ---------- Ship now: place the next order immediately (Appstle attempt-billing) ----------
     One-click, no confirm. 1) GET top-orders for the contract to find the upcoming billing attempt,
     2) PUT attempt-billing/<id> to create the order now. Requires the shop's Appstle
     "enableImmediatePlaceOrder" permission (else the PUT returns 403). Guarded against double-fire. */
  /* Ship-now double-order guard + block-scoped processing overlay (Appstle commits the order ~60s later,
     so the billing date won't move on an immediate reload — members re-tap thinking it failed). */
  function snGet(name, dflt) { var r = document.querySelector("[data-lrc-mp-subscription]"); var v = r && r.getAttribute(name); return (v != null && v !== "") ? v : dflt; }
  function snActiveCid() { var b = window.LRCMP_SUBS && window.LRCMP_SUBS.contracts && window.LRCMP_SUBS.contracts[window.LRCMP_SUB_ACTIVE || 0]; var d = (b && b.dto) || {}; return String(d.subscriptionContractId || d.contractId || d.id || ""); }
  function snLockKey(cid) { return "lrc-shipnow-lock:" + String(cid); }
  function snCooldownMs() { var m = parseFloat(snGet("data-shipnow-cooldown-minutes", "5")); if (isNaN(m)) m = 5; return m * 60000; }
  function snLockActive(cid) {
    if (!cid) return false;
    try {
      var raw = localStorage.getItem(snLockKey(cid));
      if (!raw) return false;
      var ts = parseInt(raw, 10);
      if (isNaN(ts)) { localStorage.removeItem(snLockKey(cid)); return false; }
      if (Date.now() - ts < snCooldownMs()) return true;
      localStorage.removeItem(snLockKey(cid));          /* expired → clean up */
      return false;
    } catch (e) { return false; }
  }
  function snSetLock(cid) { try { localStorage.setItem(snLockKey(cid), String(Date.now())); } catch (e) {} }
  function snButtons() { var s = document.querySelector("[data-lrc-mp-subscription]"); return s ? Array.prototype.slice.call(s.querySelectorAll(".dropacts .btn-primary")) : []; }
  /* Applied on load + on every data event: the static Liquid button isn't re-rendered, so we mutate it in place. */
  function snApplyLockState() {
    var locked = snLockActive(snActiveCid());
    var lockLabel = snGet("data-shipnow-cooldown-label", "Order placed just now");
    snButtons().forEach(function (btn) {
      if (btn.classList.contains("sn-working")) return;   /* mid-flight — leave it */
      if (locked) {
        if (!btn.getAttribute("data-sn-orig")) btn.setAttribute("data-sn-orig", btn.textContent);
        btn.disabled = true; btn.setAttribute("aria-disabled", "true"); btn.classList.add("sn-locked");
        btn.style.touchAction = "manipulation"; btn.textContent = lockLabel;
      } else if (btn.classList.contains("sn-locked")) {
        btn.disabled = false; btn.removeAttribute("aria-disabled"); btn.classList.remove("sn-locked");
        var o = btn.getAttribute("data-sn-orig"); if (o == null) o = snGet("data-shipnow-label", "Ship it now");
        btn.textContent = o;
      }
    });
  }
  function snUnmountOverlay() { var host = document.querySelector("[data-lrc-mp-subscription]"); if (!host) return; var ov = host.querySelector(".sn-overlay"); if (ov && ov.parentNode) ov.parentNode.removeChild(ov); host.classList.remove("sn-anchor"); }
  function snMountOverlay() {
    var host = document.querySelector("[data-lrc-mp-subscription]"); if (!host) return;
    if (host.querySelector(".sn-overlay")) return;        /* idempotent */
    host.classList.add("sn-anchor");
    var ov = document.createElement("div"); ov.className = "sn-overlay"; ov.setAttribute("role", "status"); ov.setAttribute("aria-live", "polite");
    ov.innerHTML = '<div class="sn-card"><div class="sn-eyebrow"><span class="sn-beacon"></span><span class="sn-h"></span></div><div class="sn-b"></div><div class="sn-actions" data-sn-actions></div></div>';
    ov.querySelector(".sn-h").textContent = snGet("data-shipnow-processing-headline", "Your order is being placed!");
    ov.querySelector(".sn-b").textContent = snGet("data-shipnow-processing-body", "This can take up to 60 seconds to reflect in your account.");
    host.appendChild(ov);
    var waitS = parseInt(snGet("data-shipnow-wait-seconds", "60"), 10); if (isNaN(waitS)) waitS = 60;
    /* refresh appears ONLY after the wait — never auto-refresh */
    setTimeout(function () {
      var actions = ov.querySelector("[data-sn-actions]"); if (!actions || actions.firstChild) return;
      var rb = document.createElement("button"); rb.type = "button"; rb.className = "sn-refresh"; rb.style.touchAction = "manipulation";
      rb.textContent = snGet("data-shipnow-refresh-label", "Refresh my subscription");
      rb.addEventListener("click", function () {
        snUnmountOverlay();
        try { if (typeof window.LRCMP_REFRESH_AFTER_CHANGE === "function") { Promise.resolve(window.LRCMP_REFRESH_AFTER_CHANGE()).catch(function () {}); return; } } catch (e) {}
        try { location.reload(); } catch (e) {}
      });
      actions.appendChild(rb);
    }, waitS * 1000);
  }
  document.addEventListener("lrc-mp:subscription", snApplyLockState);
  document.addEventListener("lrc-mp:sub-active", snApplyLockState);
  if (window.LRCMP_SUBS) snApplyLockState();

  var shipNowBusy = false;
  LRCMP.shipNow = function (e) {
    if (e && e.preventDefault) e.preventDefault();
    if (shipNowBusy) return false;
    var btn = e && (e.currentTarget || e.target) || null;

    var bundle = window.LRCMP_SUBS && window.LRCMP_SUBS.contracts && window.LRCMP_SUBS.contracts[window.LRCMP_SUB_ACTIVE || 0];
    var dto = (bundle && bundle.dto) || {};
    var cid = String(dto.subscriptionContractId || dto.contractId || dto.id || "");
    if (!cid) { if (window.console) console.warn("[lrc-member-portal] shipNow: no active contract id"); return false; }

    if (snLockActive(cid)) { snApplyLockState(); return false; }   /* hard guard: order already placed within cooldown */

    shipNowBusy = true;
    var origLabel = btn ? btn.textContent : "";
    if (btn) { btn.disabled = true; btn.setAttribute("aria-busy", "true"); btn.classList.add("sn-working"); btn.style.touchAction = "manipulation"; btn.innerHTML = 'Working<span class="sn-dots" aria-hidden="true"></span>'; }

    api("subscription-billing-attempts/top-orders?contractId=" + encodeURIComponent(cid))
      .then(function (list) {
        var arr = toArray(list), attemptId = null;
        for (var i = 0; i < arr.length; i++) {
          var a = arr[i]; var aid = a && (a.billingAttemptId || a.id);
          if (aid) { attemptId = aid; break; }
        }
        if (!attemptId) throw new Error("no_upcoming_attempt");
        return fetch(BASE + "subscription-billing-attempts/attempt-billing/" + encodeURIComponent(attemptId), {
          method: "PUT", credentials: "same-origin", redirect: "manual", headers: { "Accept": "application/json" }
        });
      })
      .then(function (r) {
        if (!r.ok || r.type === "opaqueredirect" || r.status === 0) {
          if (r.status === 403 && window.console) console.warn("[lrc-member-portal] shipNow: 403, shop is missing Appstle 'enableImmediatePlaceOrder' permission. Ask Appstle support to enable Immediate Place Order for this store.");
          throw new Error("shipnow_failed_" + r.status);
        }
        snSetLock(cid);                                   /* DOUBLE-ORDER LOCK — survives reloads + re-renders */
        if (btn) { btn.classList.remove("sn-working"); btn.innerHTML = ""; }
        snMountOverlay();                                 /* comfort overlay over the block; refresh appears after the wait */
        snApplyLockState();                               /* disable the button + show the cooldown label immediately */
        shipNowBusy = false;
      })
      .catch(function (err) {
        if (window.console) console.warn("[lrc-member-portal] shipNow failed:", err && err.message);
        shipNowBusy = false;
        if (btn) {
          btn.classList.remove("sn-working");             /* failure path unchanged otherwise: no overlay, no lock */
          btn.disabled = false; btn.removeAttribute("aria-busy");
          var msg = (err && err.message === "no_upcoming_attempt") ? "No upcoming order to place" : "Couldn't place order, try again";
          btn.textContent = msg;
          setTimeout(function () { if (btn) btn.textContent = origLabel || "Ship now"; }, 2600);
        }
      });
    return false;
  };

  /* ---------- cadenceModal: frequency picker → 25% Tier selling-plan swap (per line, sequential) ---------- */
  var cadState = { picked: null, current: null };                  /* picked/current = intervalCount in DAYS */
  function cadGet(name, dflt) { var r = document.querySelector("[data-lrc-mp-subscription]"); var v = r && r.getAttribute(name); return (v != null && v !== "") ? v : dflt; }
  function cadModalQ(sel) { var m = modal("cadenceModal"); return m ? m.querySelector(sel) : null; }
  function cadUpdateCta() {
    var cta = cadModalQ("[data-cad-cta]"); if (!cta) return;
    var p = cadState.picked;
    /* enabled only when a frequency is picked AND it differs from the current cadence (else no-op) */
    if (p != null && p !== cadState.current) { cta.disabled = false; cta.textContent = cadGet("data-cad-cta-template", "Save as every {days} days").replace(/\{days\}/g, p); }
    else { cta.disabled = true; cta.textContent = cadGet("data-cad-cta-idle", "Pick a frequency"); }
  }
  /* Cadence change = move the contract onto the 25% Tier plan for the chosen cadence via ONE atomic backend
     call (action "update_contract_plan" -> subscription-contracts-update-frequency-by-selling-plan), which
     swaps plan + frequency + pricing together. Mirrors the Stash upgrade (assets/lrc-mp-stash.js). Replaces the
     old update_contract_intervals + per-line update_line_selling_plan sequence, which landed the wrong (10%)
     tier and false-reported failure on Appstle's "500 but saved" race. Customers self-migrate to 25%. */
  var CAD_TIER25 = { 30: "692161872185", 45: "692161904953", 60: "692161937721", 75: "692161970489", 90: "692168458553" };
  var CAD_PREPAID = ["702230561081", "702230593849"];   /* Stash 6mo / 12mo — cadence locked during prepay */
  async function cadCommit(cta) {
    if (cadState.picked == null || cadState.picked === cadState.current) return;   /* defensive — button should be disabled */
    var bundle = window.LRCMP_SUBS && window.LRCMP_SUBS.contracts && window.LRCMP_SUBS.contracts[window.LRCMP_SUB_ACTIVE || 0];
    var dto = (bundle && bundle.dto) || {};
    var cid = String(dto.subscriptionContractId || dto.contractId || dto.id || "");
    var days = cadState.picked;
    var lines = (bundle && bundle.contract && bundle.contract.lines && (bundle.contract.lines.nodes || bundle.contract.lines)) || [];
    function failMsg(msg) { cta.disabled = true; cta.textContent = msg; setTimeout(function () { cadUpdateCta(); }, 2500); }
    if (!cid || !lines.length) { failMsg(cadGet("data-cad-error", "Couldn't update frequency. Nothing was changed.")); return; }
    /* Prepaid (Stash) lockout — cadence is fixed during prepay */
    var firstPlan = String((lines[0] && (lines[0].sellingPlanId || (lines[0].sellingPlan && lines[0].sellingPlan.id))) || "").split("/").pop();
    if (CAD_PREPAID.indexOf(firstPlan) !== -1) { failMsg(cadGet("data-cad-prepaid-lock", "Cadence is locked during prepay. Adjust at the end of your prepaid cycle.")); return; }
    var targetPlanId = CAD_TIER25[days];
    if (!targetPlanId) { failMsg(cadGet("data-cad-error", "Couldn't update frequency. Nothing was changed.")); return; }
    cta.disabled = true; cta.textContent = cadGet("data-cad-working", "Updating…");
    try {
      /* Atomic contract-level swap: ONE Appstle call sets plan + frequency + pricing (modeled on the Stash flow).
         Backend returns HTTP 200 { ok:false, error, detail } on Appstle rejection (the proxy strips non-2xx), so
         treat as failure ONLY on !cpr.ok OR body ok===false. Never throw on a 500 whose body reports success. */
      var cpr = await fetch("/apps/lrc", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_contract_plan", contractId: cid, sellingPlanId: targetPlanId }) });
      if (!cpr.ok) { var cpt = await cpr.text(); throw new Error("plan " + cpr.status + " " + String(cpt).slice(0, 200)); }
      var cpj = await cpr.json().catch(function () { return null; });
      if (!cpj || cpj.ok === false) {
        throw new Error("plan " + ((cpj && cpj.error) || "not-ok") + " :: " + ((cpj && cpj.detail) ? String(cpj.detail).slice(0, 240) : ""));
      }
      cta.textContent = cadGet("data-cad-success-template", "Frequency saved — every {days} days").replace(/\{days\}/g, days);
      await (window.LRCMP_REFRESH_AFTER_CHANGE ? window.LRCMP_REFRESH_AFTER_CHANGE() : location.reload());
      if (window.LRCMP && LRCMP.close) LRCMP.close("cadenceModal");
    } catch (err) {
      if (window.console) console.error("[lrc-member-portal] cadence failed:", err && err.message);
      cta.textContent = cadGet("data-cad-error", "Couldn't update frequency. Nothing was changed.");
      setTimeout(function () { cadUpdateCta(); }, 2500);                          /* revert to actionable state so the user can retry */
    }
  }
  (function attachCadence() {
    var m = modal("cadenceModal"); if (!m) return;
    m.addEventListener("click", function (ev) {
      var t = ev.target; if (!t || !t.closest) return;
      var cta = t.closest("[data-cad-cta]"); if (cta && !cta.disabled) { cadCommit(cta); return; }
    });
  })();

  /* ---------- addrModal: shipping-address form + update-shipping-address write ---------- */
  function adrGet(name, dflt) { var r = document.querySelector("[data-lrc-mp-subscription]"); var v = r && r.getAttribute(name); return (v != null && v !== "") ? v : dflt; }
  function adrModalQ(sel) { var m = modal("addrModal"); return m ? m.querySelector(sel) : null; }
  function adrField(name) { return adrModalQ('[data-addr="' + name + '"]'); }
  function adrSetVal(name, val) { var el = adrField(name); if (el) el.value = (val == null ? "" : String(val)); }
  function adrOptions(list, sel) { return list.map(function (o) { return '<option value="' + o.code + '"' + (o.code === sel ? " selected" : "") + ">" + esc(o.name) + "</option>"; }).join(""); }
  function adrRenderProvinces(countryCode, selProv) { var sel = adrField("provinceCode"); if (sel) sel.innerHTML = adrOptions(ADDR_PROVINCES[countryCode] || [], selProv || ""); }
  function adrClearErrors() {
    var m = modal("addrModal"); if (!m) return;
    Array.prototype.forEach.call(m.querySelectorAll(".field-error"), function (e) { e.style.display = "none"; e.textContent = ""; });
    Array.prototype.forEach.call(m.querySelectorAll(".inp"), function (i) { i.classList.remove("invalid"); });
  }
  function adrShowError(name, msg) {
    var f = adrField(name); if (f) f.classList.add("invalid");
    var e = adrModalQ('[data-err="' + name + '"]'); if (e) { e.textContent = msg; e.style.display = "block"; }
  }
  function adrValidate() {
    adrClearErrors();
    var ok = true, reqMsg = adrGet("data-adr-validation-required", "Required"), zipMsg = adrGet("data-adr-validation-zip", "Enter a valid ZIP/postal code"), phoneMsg = adrGet("data-adr-validation-phone", "Enter a valid phone number with at least 10 digits");
    ["firstName", "lastName", "address1", "city", "countryCode", "provinceCode", "zip", "phone"].forEach(function (k) {
      var el = adrField(k), v = el ? String(el.value || "").trim() : "";
      if (!v) { adrShowError(k, reqMsg); ok = false; }
    });
    var cc = (adrField("countryCode") || {}).value || "", zip = String((adrField("zip") || {}).value || "").trim();
    if (zip) {
      var bad = (cc === "US" && !/^\d{5}(-\d{4})?$/.test(zip)) || (cc === "CA" && !/^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/.test(zip));
      if (bad) { adrShowError("zip", zipMsg); ok = false; }
    }
    var phone = String((adrField("phone") || {}).value || "");
    if (phone.trim() && phone.replace(/\D/g, "").length < 10) { adrShowError("phone", phoneMsg); ok = false; }   /* required-empty already flagged above */
    return ok;
  }
  function adrPayload() {
    function v(k) { var el = adrField(k); return el ? String(el.value || "").trim() : ""; }
    return { methodType: "SHIPPING", firstName: v("firstName"), lastName: v("lastName"),
      address1: v("address1"), address2: v("address2"), city: v("city"), provinceCode: v("provinceCode"),
      countryCode: v("countryCode"), zip: v("zip"), phone: v("phone") };
  }
  function adrCommit(cta) {
    if (!adrValidate()) return;                                                  /* inline errors shown; no API call */
    var subs = (window.LRCMP_SUBS && window.LRCMP_SUBS.contracts) || [];
    var applyAll = !!(adrModalQ("#adrApplyAll") && adrModalQ("#adrApplyAll").checked);
    var ids = [];
    if (applyAll) { subs.forEach(function (c) { var id = c && c.dto && (c.dto.subscriptionContractId || c.dto.contractId || c.dto.id); if (id) ids.push(String(id)); }); }
    else { var act = subs[window.LRCMP_SUB_ACTIVE || 0]; var id = act && act.dto && (act.dto.subscriptionContractId || act.dto.contractId || act.dto.id); if (id) ids.push(String(id)); }
    if (!ids.length) { cta.textContent = adrGet("data-adr-error", "Couldn't update the address. Nothing was changed."); return; }
    var body = JSON.stringify(adrPayload());
    if (cta.dataset.idle == null) cta.dataset.idle = adrGet("data-adr-cta-idle", "Save address");
    cta.disabled = true; cta.textContent = applyAll ? adrGet("data-adr-working-all", "Updating address on all subscriptions…") : adrGet("data-adr-working-single", "Updating address…");
    var puts = ids.map(function (cid) {
      return fetch("/apps/subscriptions/cp/api/subscription-contracts-update-shipping-address?contractId=" + encodeURIComponent(cid),
        { method: "PUT", credentials: "same-origin", redirect: "manual", headers: { "Content-Type": "application/json", "Accept": "application/json" }, body: body })
        .then(function (r) {
          var bad = !r.ok || r.type === "opaqueredirect" || r.status === 0;
          if (bad && (r.status === 401 || r.status === 404) && window.console) console.warn("[lrc-member-portal] address: cp endpoint returned " + r.status + " for " + cid + " — cp proxy may not serve update-shipping-address; backend fallback needed.");
          return { cid: cid, ok: !bad };
        })
        .catch(function () { return { cid: cid, ok: false }; });
    });
    Promise.allSettled(puts).then(function (results) {
      var oks = results.filter(function (x) { return x.status === "fulfilled" && x.value && x.value.ok; }).length, total = ids.length;
      if (oks === total) {
        cta.textContent = applyAll ? adrGet("data-adr-success-all", "Address updated on all subscriptions.") : adrGet("data-adr-success-single", "Address updated.");
        setTimeout(function () { try { location.reload(); } catch (e) {} }, 1800);
      } else if (oks > 0) {
        if (window.console) console.warn("[lrc-member-portal] address: partial success " + oks + "/" + total);
        cta.textContent = adrGet("data-adr-partial-error", "Some subscriptions couldn't be updated. Refresh to see current state.");   /* no reload — manual refresh */
      } else {
        if (window.console) console.warn("[lrc-member-portal] address: all " + total + " update(s) failed");
        cta.textContent = adrGet("data-adr-error", "Couldn't update the address. Nothing was changed.");
        setTimeout(function () { cta.disabled = false; cta.textContent = cta.dataset.idle || "Save address"; }, 2500);
      }
    });
  }
  (function attachAddr() {
    var m = modal("addrModal"); if (!m) return;
    m.addEventListener("change", function (ev) { var t = ev.target; if (t && t.matches && t.matches('[data-addr="countryCode"]')) adrRenderProvinces(t.value, ""); });
    m.addEventListener("click", function (ev) { var t = ev.target; if (!t || !t.closest) return; var cta = t.closest("[data-adr-cta]"); if (cta && !cta.disabled) { adrCommit(cta); return; } });
  })();

  /* ---------- payModal: current card display + email-link payment update (Appstle is email-only — no inline link) ---------- */
  function payGet(name, dflt) { var r = document.querySelector("[data-lrc-mp-subscription]"); var v = r && r.getAttribute(name); return (v != null && v !== "") ? v : dflt; }
  function payModalQ(sel) { var m = modal("payModal"); return m ? m.querySelector(sel) : null; }
  function payRegion(name) { return payModalQ('[data-mp-region="' + name + '"]'); }
  function payCap(s) { s = String(s || ""); return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s; }
  function payReset() {
    var suc = payRegion("pay-success"); if (suc) suc.style.display = "none";
    var err = payRegion("pay-error-slot"); if (err) { err.style.display = "none"; err.textContent = ""; }
    var card = payRegion("pay-methods"); if (card) card.style.display = "";
    var cta = payModalQ("[data-pay-cta]"); if (cta) { cta.style.display = ""; cta.disabled = false; cta.textContent = payGet("data-pay-cta-idle", "Email Me A Secure Link"); }
  }
  function payCommit(cta) {
    var bundle = window.LRCMP_SUBS && window.LRCMP_SUBS.contracts && window.LRCMP_SUBS.contracts[window.LRCMP_SUB_ACTIVE || 0];
    var dto = (bundle && bundle.dto) || {};
    var cid = String(dto.subscriptionContractId || dto.contractId || dto.id || "");
    var errEl = payRegion("pay-error-slot"); if (errEl) { errEl.style.display = "none"; errEl.textContent = ""; }
    if (!cid) { if (errEl) { errEl.textContent = payGet("data-pay-error", "Couldn't send the email. Please try again."); errEl.style.display = ""; } return; }
    try {
      var _pf = { action:"record_pay_link_requested", contractId: cid,
        email:(dto.customerEmail||dto.email||(dto.customer&&dto.customer.email)||""),
        source:(window.__lrcPayFrom||"member_portal") };
      fetch("/apps/lrc",{method:"POST",credentials:"same-origin",headers:{"Content-Type":"application/json"},body:JSON.stringify(_pf)}).catch(function(){});
    } catch(e){}
    cta.disabled = true; cta.textContent = payGet("data-pay-working", "Sending email…");
    var url = "/apps/subscriptions/cp/api/subscription-contracts-update-payment-method?contractId=" + encodeURIComponent(cid);
    fetch(url, { method: "PUT", credentials: "same-origin", redirect: "manual", headers: { "Content-Type": "application/json", "Accept": "application/json" } })
      .then(function (r) {
        if (!r.ok || r.type === "opaqueredirect" || r.status === 0) {
          if ((r.status === 401 || r.status === 404) && window.console) console.warn("[lrc-member-portal] payment: cp endpoint returned " + r.status + " — cp proxy may not serve update-payment-method; backend fallback needed.");
          throw new Error("payment_failed_" + r.status);
        }
        /* success: swap to the success state — only an email was triggered, so NO reload */
        var shh = payRegion("pay-success-h"); if (shh) shh.textContent = payGet("data-pay-success-headline", "Check your email");
        var sbb = payRegion("pay-success-b"); if (sbb) sbb.textContent = payGet("data-pay-success-body", "We sent a secure link to update your card. It may arrive within a minute. The link works on any device.");
        var card = payRegion("pay-methods"); if (card) card.style.display = "none";
        if (errEl) errEl.style.display = "none";
        cta.style.display = "none";
        var suc = payRegion("pay-success"); if (suc) suc.style.display = "";
      })
      .catch(function (err) {
        if (window.console) console.warn("[lrc-member-portal] payment failed:", err && err.message);
        if (errEl) { errEl.textContent = payGet("data-pay-error", "Couldn't send the email. Please try again."); errEl.style.display = ""; }
        setTimeout(function () { cta.disabled = false; cta.textContent = payGet("data-pay-cta-idle", "Email Me A Secure Link"); }, 2500);
      });
  }
  (function attachPay() {
    var m = modal("payModal"); if (!m) return;
    m.addEventListener("click", function (ev) { var t = ev.target; if (!t || !t.closest) return; var cta = t.closest("[data-pay-cta]"); if (cta && !cta.disabled) { payCommit(cta); return; } });
  })();

  /* ===================== GUIDED TOUR (config-driven; anchor-and-track beacon) =====================
     Config comes from the lrc-mp-tour section's <script id="lrcmp-tour-config">. Targets live in OTHER
     portal sections (outside this engine root), so target lookups use document.querySelector. The tip
     box lives inside root, so its regions use region(). */
  function tourConfig() {
    try {
      var el = document.getElementById("lrcmp-tour-config");
      if (!el) return { enabled: false, stops: [] };
      var c = JSON.parse(el.textContent || "{}");
      c.stops = Array.isArray(c.stops) ? c.stops : [];
      return c;
    } catch (e) { return { enabled: false, stops: [] }; }
  }
  function tourElFor(stop) { try { return (stop && stop.target) ? document.querySelector(stop.target) : null; } catch (e) { return null; } }
  function tourQuery(sel) { try { return sel ? document.querySelector(sel) : null; } catch (e) { return null; } }
  function tourVisible(el) { if (!el) return false; var r = el.getBoundingClientRect(); return r.width > 0 && r.height > 0; }
  function tourBeaconEl() { return document.getElementById("lrcmpTourBeacon"); }
  function tourTipEl() { return document.getElementById("lrcmpTourTip"); }

  var TOUR_CFG = null, currentStops = [], currentIdx = 0, endScreen = null, onEndScreen = false;
  var tourRepoEl = null, tourDotEl = null, tourAnchor = "center", tourTrackRAF = null, tourScrollH = null, tourResizeH = null, tourLastBox = null;

  /* single shared positioner — places the BEACON on dotEl and the TIP relative to the MAIN target mainEl
     (below, flipping above only if it would run off the bottom). dotEl falls back to mainEl when no finer
     beacon target was resolved. The 16px beacon is positioned by its top-left.
     anchor: "corner" (DEFAULT) = straddle the element's top-right corner; "center" = element centre;
             "right" = just outside the element's right edge, vertically centred (beside, not covering). */
  function reposition(mainEl, dotEl, anchor) {
    if (onEndScreen) return;   /* the centered end screen owns its position via .tt-centered — never overwrite it */
    dotEl = dotEl || mainEl;
    if (!dotEl) return;
    var dr = dotEl.getBoundingClientRect();
    /* beacon dot (16px) top-left, per anchor — computed ONCE and reused so the tip can follow the beacon */
    var bx, by;
    if (anchor === "center") { bx = dr.left + dr.width / 2 - 8; by = dr.top + dr.height / 2 - 8; }
    else if (anchor === "right") { bx = dr.right + 8; by = dr.top + dr.height / 2 - 8; }
    else { /* corner (default) */ bx = dr.right - 8; by = dr.top - 8; }
    var bc = tourBeaconEl();
    if (bc) { bc.style.left = bx + "px"; bc.style.top = by + "px"; }
    var tip = tourTipEl();
    if (tip && mainEl) {
      /* tip is already .show, so offsetHeight/Width are real. The tip follows the BEACON (not the element's
         left edge) so on wide full-width targets the tip stays next to the right-corner dot instead of drifting
         to the far-left of the element. Clamped to the viewport so it never runs off either edge. */
      var th = tip.offsetHeight || 180, tw = tip.offsetWidth || 300;
      var beaconCx = bx + 8;   /* beacon dot centre X */
      /* HORIZONTAL: centre the tip on the beacon, clamped on-screen */
      var left = Math.min(Math.max(12, beaconCx - tw / 2), window.innerWidth - tw - 12);
      /* VERTICAL: below the beacon element; flip above only if below would overflow the viewport bottom */
      var top = dr.bottom + 12;
      if (top + th > window.innerHeight) top = Math.max(12, dr.top - th - 12);
      tip.style.transform = "";   /* clear any centred-screen transform from a prior centered stop */
      tip.style.left = left + "px";
      tip.style.top = top + "px";
    }
  }
  function tourBoxKey(r) { return Math.round(r.top) + "|" + Math.round(r.left) + "|" + Math.round(r.width) + "|" + Math.round(r.height); }

  /* tracking loop: while the tour is open, follow the current target/dot if either moves or resizes */
  function tourTrackTick() {
    if (!tourRepoEl) { tourTrackRAF = null; return; }
    var key = tourBoxKey(tourRepoEl.getBoundingClientRect()) + "~" + (tourDotEl ? tourBoxKey(tourDotEl.getBoundingClientRect()) : "");
    if (key !== tourLastBox) { tourLastBox = key; reposition(tourRepoEl, tourDotEl, tourAnchor); }
    tourTrackRAF = window.requestAnimationFrame(tourTrackTick);
  }
  function tourStartTracking(mainEl, dotEl, anchor) {
    tourRepoEl = mainEl; tourDotEl = dotEl || mainEl; tourAnchor = anchor || "center"; tourLastBox = null;
    if (!tourScrollH) { tourScrollH = function () { if (tourRepoEl) reposition(tourRepoEl, tourDotEl, tourAnchor); }; window.addEventListener("scroll", tourScrollH, true); }
    if (!tourResizeH) { tourResizeH = function () { if (tourRepoEl) reposition(tourRepoEl, tourDotEl, tourAnchor); }; window.addEventListener("resize", tourResizeH); }
    if (tourTrackRAF == null) tourTrackRAF = window.requestAnimationFrame(tourTrackTick);
  }
  function tourStopTracking() {
    tourRepoEl = null; tourDotEl = null; tourAnchor = "center";
    if (tourTrackRAF != null) { window.cancelAnimationFrame(tourTrackRAF); tourTrackRAF = null; }
    if (tourScrollH) { window.removeEventListener("scroll", tourScrollH, true); tourScrollH = null; }
    if (tourResizeH) { window.removeEventListener("resize", tourResizeH); tourResizeH = null; }
  }

  /* wait for el's rect to settle (2 stable frames) or 1200ms timeout — handles async render + scroll motion */
  function tourWaitSettle(el, cb) {
    var start = Date.now(), prev = null, stable = 0;
    (function poll() {
      var key = tourBoxKey(el.getBoundingClientRect());
      if (key === prev) { stable++; } else { stable = 0; prev = key; }
      if (stable >= 2 || (Date.now() - start) > 1200) { cb(); return; }
      window.requestAnimationFrame(poll);
    })();
  }
  function tourClearSpot() { Array.prototype.forEach.call(document.querySelectorAll(".tour-spot"), function (e) { e.classList.remove("tour-spot"); }); }

  /* fill the tip copy (direct textContent so empty values don't render the setText "—" placeholder) */
  function tourFillTip(stop) {
    var ey = region("tour-eyebrow"); if (ey) ey.textContent = stop.eyebrow || (TOUR_CFG && TOUR_CFG.eyebrowDefault) || "";
    var hd = region("tour-headline"); if (hd) hd.textContent = stop.headline || "";
    var bd = region("tour-body"); if (bd) bd.textContent = stop.body || "";
    var nm = region("tour-num"); if (nm) nm.textContent = "STEP " + (currentIdx + 1) + " OF " + currentStops.length;
  }
  /* nav controls: back hidden at first stop; next becomes "Done" on the last stop; skip hidden on the last stop */
  /* nav for a BEACON stop. Next becomes "Done" only when this is the last beacon stop AND there is no end screen
     after it; otherwise Next stays "›" (advances to the next beacon, or to the end screen). */
  function tourNavState() {
    var finishHere = (currentIdx === currentStops.length - 1) && !endScreen;
    var back = document.querySelector("#lrcmpTourTip .tt-back"); if (back) back.style.visibility = (currentIdx > 0) ? "visible" : "hidden";
    tourSetNext(finishHere ? "done" : "next");
    var skip = document.querySelector("#lrcmpTourTip .tt-skip"); if (skip) skip.style.display = finishHere ? "none" : "";
  }
  /* style the next control as a "›" arrow or a "Done" pill */
  function tourSetNext(mode) {
    var nx = region("tour-next"); if (!nx) return;
    if (mode === "done") { nx.textContent = "Done"; nx.style.width = "auto"; nx.style.padding = "0 16px"; nx.style.borderRadius = "17px"; nx.setAttribute("aria-label", "Done"); }
    else { nx.textContent = "›"; nx.style.width = ""; nx.style.padding = ""; nx.style.borderRadius = ""; nx.setAttribute("aria-label", "Next"); }
  }

  /* go to BEACON stop i (currentStops holds ONLY the counted beacon stops — never the end screen) */
  function tourGo(i) {
    if (!currentStops.length) { endTour(); return; }
    if (i < 0) i = 0;
    if (i >= currentStops.length) { endTour(); return; }
    onEndScreen = false;
    /* advance over any stop whose target went missing since startTour (defensive) */
    var n = currentStops.length, el = null;
    while (i < n) { el = tourElFor(currentStops[i]); if (el) break; i++; }
    if (i >= n) { endTour(); return; }
    currentIdx = i;
    var stop = currentStops[i];
    tourClearSpot();
    tourStopTracking();
    /* scroll to the target, then anchor the beacon + tip after the rect settles */
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    tourWaitSettle(el, function () {
      el.classList.add("tour-spot");
      tourFillTip(stop);
      /* beacon dot color per stop: light (for dark bg) vs dark (for light bg) */
      var bc = tourBeaconEl(); if (bc) { bc.style.background = (stop.dot === "light") ? "#EFD5C5" : "#A3663E"; bc.classList.add("show"); }
      tourNavState();
      var tip = tourTipEl(); if (tip) { tip.classList.remove("tt-centered"); tip.classList.add("show"); }   /* beacon stop: drop end-screen centering */
      /* finer beacon target (e.g. a tile's Redeem button) falls back to the main target if not rendered yet */
      var dotEl = (stop.beaconTarget && tourQuery(stop.beaconTarget)) || el;
      var anchor = stop.beaconAnchor || "corner";
      reposition(el, dotEl, anchor);          /* tip follows the beacon; dot rides dotEl */
      tourStartTracking(el, dotEl, anchor);
    });
  }

  /* the END SCREEN — a separate, UNCOUNTED closing state: centred, no beacon, no spotlight, no counter;
     BACK returns to the last beacon stop, DONE closes + scrolls to top, no skip. */
  function showEndScreen() {
    if (!endScreen) { tourDone(); return; }
    onEndScreen = true;
    tourClearSpot();
    tourStopTracking();
    var bc0 = tourBeaconEl(); if (bc0) bc0.classList.remove("show");
    tourFillTip(endScreen);
    var nm = region("tour-num"); if (nm) nm.textContent = "";   /* no counter on the end screen */
    var back = document.querySelector("#lrcmpTourTip .tt-back"); if (back) back.style.visibility = "visible";   /* → last beacon stop */
    tourSetNext("done");
    var skip = document.querySelector("#lrcmpTourTip .tt-skip"); if (skip) skip.style.display = "none";
    var tip0 = tourTipEl();
    if (tip0) {
      /* clear any inline px position a prior beacon stop left behind, then let .tt-centered own the centering
         (inline transform alone loses to the .show rise animation's transform:none final keyframe). */
      tip0.style.left = ""; tip0.style.top = ""; tip0.style.transform = "";
      tip0.classList.add("tt-centered");
      tip0.classList.add("show");
    }
  }

  function startTour() {
    TOUR_CFG = tourConfig();
    if (!TOUR_CFG.enabled) { return; }
    var stops = TOUR_CFG.stops || [];
    /* SPLIT: counted beacon stops (centered !== true AND target present) vs. the end screen (first centered, uncounted) */
    currentStops = []; endScreen = null;
    for (var k = 0; k < stops.length; k++) {
      var s2 = stops[k];
      if (s2 && s2.centered) { if (!endScreen) endScreen = s2; continue; }   /* end screen = first centered; extras ignored */
      var e = tourElFor(s2); if (e && tourVisible(e)) currentStops.push(s2);
    }
    /* edge case: no present beacon stops → don't run at all (never show a lone end screen) */
    if (!currentStops.length) { return; }
    onEndScreen = false;
    currentIdx = 0;
    var o = document.getElementById("lrcmpTourOverlay"); if (o) o.classList.add("show");
    tourGo(0);
  }
  function tourNext() {
    if (onEndScreen) { tourDone(); return; }
    if (currentIdx < currentStops.length - 1) { tourGo(currentIdx + 1); return; }
    /* last beacon stop: go to the end screen if one exists, else finish */
    if (endScreen) showEndScreen(); else tourDone();
  }
  function tourBack() {
    if (onEndScreen) { tourGo(currentStops.length - 1); return; }   /* end screen → last beacon stop (restores beacon + counter) */
    if (currentIdx > 0) tourGo(currentIdx - 1);
  }
  /* Done = the intentional finish on the last stop: close the tour AND smooth-scroll the page to top.
     (Skip / overlay-click only close — they do NOT scroll.) */
  function tourDone() {
    /* Phase 4: tour has no backend handler, so stamp its onboarding item done HERE — the genuine completion
       (Done on the end screen / finishing the last stop). Skip + overlay-click go through endTour() instead and
       must NOT mark it done. Fire-and-forget: the close/scroll UX never waits on the network. Fired before
       endTour() so it's initiated regardless of teardown. */
    try { fetch("/apps/lrc", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "onboarding_complete_item", item: "tour" }) }).then(function () { try { document.dispatchEvent(new CustomEvent("lrc:onboarding-changed")); } catch (e) {} }).catch(function () {}); } catch (e) {}
    endTour();
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) { window.scrollTo(0, 0); }
  }
  function endTour() {
    tourStopTracking();
    tourClearSpot();
    var t = tourTipEl(); if (t) { t.style.transform = ""; t.style.left = ""; t.style.top = ""; t.classList.remove("tt-centered"); }   /* clear centred-screen state so a fresh run is clean */
    ["lrcmpTourOverlay", "lrcmpTourTip", "lrcmpTourBeacon"].forEach(function (id) { var e = document.getElementById(id); if (e) e.classList.remove("show"); });
  }
  var ov = document.getElementById("lrcmpTourOverlay"); if (ov) ov.addEventListener("click", endTour);

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot); else boot();
})();
